import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { AgreementType } from "../shared/AgreementType.ts";
import { CostType } from "../shared/CaseConfiguration.ts";
import { ClaimKind, ClaimTypes } from "../shared/CaseModel.ts";
import { Persona } from "../shared/persona.ts";
import { guideTerms } from "./guide.ts";
import { insurerTerms, policyholderDisputeTerms } from "./insurer.ts";

const stages = {
  PreAction: "Pre-action",
  IssueStatementsOfCase: "Issue / statements of case",
  CMC: "CMC",
  Disclosure: "Disclosure",
  WitnessStatements: "Witness statements",
  ExpertReports: "Expert reports",
  PTR: "PTR",
  TrialPreparation: "Trial preparation",
  Trial: "Trial",
  Appeals: "Appeals",
  Enforcement: "Enforcement",
};

const costTypes: { [type in CostType]: string } = {
  lawFirmFee: "Law firm fees",
  courtFee: "Court fees",
  barristerFee: "Barrister fees",
  otherDisbursement: "Other disbursements",
  expertFee: "Expert fees",
};

const claimTypes = {
  [ClaimTypes.BreachOfContract]: "Breach of contract",
  [ClaimTypes.Commercial]: "Commercial",
  [ClaimTypes.Defamation]: "Defamation",
  [ClaimTypes.Fraud]: "Fraud",
  [ClaimTypes.PersonalInjury]: "Personal injury",
  [ClaimTypes.ProfessionalNegligence]: "Professional negligence",
  [ClaimTypes.Property]: "Property",
  [ClaimTypes.MedicalNegligence]: "Medical negligence",
  [ClaimTypes.ContentiousTrustAndProbate]: "Contentious trust and probate",
  [ClaimTypes.Employment]: "Employment",
  [ClaimTypes.Insolvency]: "Insolvency",
  [ClaimTypes.Competition]: "Competition",
  [ClaimTypes.Insurance]: "Insurance",
  [ClaimTypes.IntellectualProperty]: "Intellectual property",
  [ClaimTypes.GroupAction]: "Group / class action",
  [ClaimTypes.InternationalArbitration]: "International arbitration",
  [ClaimTypes.BankingAndFinancialServices]: "Banking & financial services",
  [ClaimTypes.Other]: "Other",
};

const agreementTypes: { [type in AgreementType]: string } = {
  afterTheEventInsurance: "ATE insurance",
  barristerCfa: "Barrister CFA",
  lawFirmCfa: "Law firm CFA",
  none: "No agreement",
  thirdPartyFunding: "Third-party funding",
};

const translationEn = {
  home: {
    stages,
    claimTypes,
    agreementTypes,
    greeting: {
      morning: "Good morning, {{name}}",
      afternoon: "Good afternoon, {{name}}",
      evening: "Good evening, {{name}}",
    },
    cases:
      "You have <bold>{{count}} cases</bold>, {{open}} of them not fully settled.",
    cases_one: "You have <bold>one case</bold>.",
    noCases: "You have no cases yet. Create one or import one to begin.",
    lastChange: "The last change was {{when}}, in <case>{{title}}</case>.",
    import: "Import",
    newCase: "Add case",
    search: "Go to a case by its title or a party…",
    noMatch: "No case matches",
    figures: {
      cases: "Cases",
      changedToday: "{{count}} changed today",
      open: "Not fully settled",
      partial: "{{count}} of them partially settled",
      settled: "Fully settled",
      ofCases: "of {{count}} cases",
      ofCases_one: "of one case",
      claimed: "Claimed amounts",
      acrossAll: "across all cases",
    },
    recent: {
      title: "Pick up where you left off",
      description: "The cases that were changed most recently.",
      all: "All {{count}} cases",
      all_one: "The one case",
    },
    standing: {
      title: "Where your cases stand",
      description:
        "The {{count}} cases that are not fully settled, by the stage they have reached.",
      description_one:
        "The one case that is not fully settled, at the stage it has reached.",
      allSettled: "Every case is fully settled.",
      settlement: "Settlement",
      none: "Not settled",
      partial: "Partially settled",
      full: "Fully settled",
    },
    types: {
      title: "Claim types",
      more: "{{count}} more claim types",
      more_one: "One more claim type",
      show: "Show the cases with a claim of the type {{type}}",
    },
    funding: {
      title: "Agreements",
      none: "No agreement",
      note: "A case with several agreements counts for each.",
      show: "Show the cases with this agreement: {{type}}",
    },
    // the names of the areas of the app, which the breadcrumbs carry too
    section: {
      cases: { title: "Cases" },
      portfolio: { title: "Portfolio" },
      testimonial: { title: "Testimonials" },
    },
    casesArea:
      "Find a case, carry on where you left off, and see where your cases stand.",
    portfolio: {
      description:
        "Your {{count}} cases taken together, for the firm and for your clients.",
      description_one: "Your one case, for the firm and for your clients.",
      failed: "The figures of the portfolio could not be loaded.",
      firm: {
        label: "Firm",
        costs: "of your clients' costs incurred, in {{count}} active cases",
        costs_one: "of your clients' costs incurred, in one active case",
        noActiveCases: "No active cases",
        revenueEarned: "Revenue earned",
        earned: "earned",
        toCome: "still to come",
      },
      clients: {
        label: "Clients",
        expectedValue:
          "expected value for your clients, in {{count}} cases not fully settled",
        expectedValue_one:
          "expected value for your clients, in the one case not fully settled",
        allSettled:
          "Every case is fully settled: no expected value or win chance is left to state.",
        winChance: "Average win chance",
        winChanceBin: "{{count}} clients with a win chance of {{range}}",
        winChanceBin_one: "One client with a win chance of {{range}}",
        claimed: "Claimed amounts",
        claimedBy: "by your clients",
        claimedAgainst: "against them",
        lossChance: "Chance that their net results come to a loss",
      },
      settlements: {
        label: "Settlements",
        of: "of {{count}}",
        settledCases: "cases with an accepted settlement",
        settledCases_one: "case with an accepted settlement",
        count: "{{count}} settlements",
        count_one: "one settlement",
        opponents: "with {{count}} opponents",
        opponents_one: "with one opponent",
        none: "Your clients reached no settlements yet.",
        received: "Received by your clients",
        worstCase: "Worst-case net result improved",
        costsAvoided: "Costs avoided by settling",
      },
    },
    testimonials: {
      more: "More testimonials",
    },
    copyright: "Copyright © {{year}} Legal Tech Vault GmbH.",
  },
  case: {
    stages,
    budget: {
      title: "Costs",
      description:
        "Each party's budget by stage of the litigation, and how much of it has been spent so far.",
      noBudget: "No cost configuration for {{name}}",
      section: {
        editor: "Cost editor",
        progress: "Cost progress",
        structure: "Cost breakdown",
      },
      warning: {
        noCostsDefined: "No costs defined",
      },
      progress: {
        summary: "{{incurred}} of {{stage}} costs incurred",
        allClosed: "Every stage is closed",
        ofBudget: "{{share}} of the budget incurred.",
        notToBeSpent:
          "{{share}} of it is no longer to be spent: settlements took it out of the case, so the cost progress cannot reach 100%.",
        tooltip:
          "Use the slider under the chart to change the cost progress of the open stage. To go on to the next stage, close this one; to go back, reopen the stage closed last.",
      },
      vat: {
        reclaims: "{{name}} can reclaim the VAT on its costs",
        rate: "VAT rate",
        reclaimsHelp:
          "Usual for a business registered for VAT: VAT costs the party nothing and is left out.",
        paysHelp:
          "Usual for an individual: VAT is added to its law firm fees, barrister fees, expert fees and other disbursements, but not to court fees, both in what it pays and in what it recovers from a losing opponent.",
      },
      costCopyDialog: {
        title: "Copy costs",
        description:
          "Costs of {{target}} can be populated with values of another party. Please note that this will overwrite any costs currently configured for {{target}}.",
        source: {
          description: "Select party to copy costs of",
          label: "Copy from",
          empty: "Source party must be selected",
        },
        cancel: "Cancel",
        submit: "Copy costs",
      },
      criteria: {
        byCostType: "By cost type",
        bySpending: "By spending",
      },
      table: {
        header: {
          stage: "Work done",
        },
        edit: "Edit",
        cancel: "Cancel",
        save: "Save costs",
        copy: "Copy from other party",
        // what settlements dropped of a stage, which what is entered comes on top of
        fixed: "{{amount}} fixed by settlements",
        // the same, of a party that left the case with its last settlement
        leftTheCase: "{{amount}} fixed: left the case at {{stage}}",
        error: {
          save: "Saving costs failed",
        },
      },
      stage: {
        header: {
          totalLawyerCosts: "Law firm fees",
          total: "Total",
          disbursements: "Disbursements",
        },
      },
      noParties: "Costs are defined for parties. Please add parties first",
      goToParties: "Go to parties section",
    },
    feeStructure: {
      title: "Fee structure",
      description: "Client fee structure",
      marginCaption: "What remains of the fee after indirect and direct costs",
      cap: "At most {{max}}",
      info: "Structure of your law firm's costs across client parties in the case",
      secondaryLabel: "of fee",
      indirectCosts: "Indirect costs",
      directCosts: "Direct costs",
      margin: "Profit",
      // before a structure is entered the sliders rest at 0%, which nobody chose
      notEntered: "Not entered",
      notSet: "Not set",
      missing: {
        title: "No fee structure entered",
        text: "Your firm's profit isn't calculated without it.",
      },
    },
    creator: {
      label: "Add case",
      title: "Title",
      titleHelperText: "Title must not be empty",
      submit: "Add case",
      cancel: "Cancel",
    },
    // the dates of a case and its stages (see shared/timeline.ts)
    timeline: {
      title: "Timeline",
      intro:
        "When the case started and how long you expect each stage to take. Only what you enter is planned: a stage without a duration has no expected end, and nor has any stage that counts from it.",
      dates: "Dates",
      date: {
        start: "Start of the case",
        lossDate: "Date of loss",
        notifiedDate: "Date notified",
      },
      overdue:
        "{{stage}} is overdue: you expected it to end on {{date}}, and it is still open. Enter how long it now takes, or close it. The stages after it keep the dates you planned until you do.",
      close: "Close {{stage}}",
      closeStage: "Close stage",
      reopen: "Reopen {{stage}}",
      reopenStage: "Reopen stage",
      editJudgment: "Edit the judgment",
      assessmentOutstanding: "Cost assessment outstanding",
      passedOver: "Passed over",
      notReached: "Not reached: settled",
      enterDateOf: "The day {{stage}} ended",
      asExpected: "As expected",
      asExpectedToCome:
        "Expected to end on {{date}}, which is still to come. A stage cannot end after today.",
      notBefore: "Not before {{date}}",
      notAfter: "Not after {{date}}",
      reopening: {
        title: "Reopen {{stage}}?",
        question:
          "{{stage}} is open again: its end date goes, its costs stay as they are. Continue?",
      },
      stages: "Stages",
      column: {
        stage: "Stage",
        duration: "Duration",
        expectedEnd: "Expected end",
        ended: "Ended",
        status: "Status",
      },
      months: "months",
      durationOf: "Duration of {{stage}} in months",
      status: {
        closed: "Closed",
        closedUndated: "Closed, date not entered",
        settled: "Settled",
        open: "Open",
        overdue: "Overdue",
        overdueSince: "Overdue since {{date}}",
        planned: "Planned",
        afterOverdue: "After an overdue stage",
        notPlanned: "Not planned",
      },
      explanation:
        "An expected end is the end of the stage before, or the start of the case, plus the duration you entered. A closed stage keeps the date it ended on.",
      chart: {
        title: "Stages over time",
        legend:
          "Closed stages in the dark colour, the open one in grey, overdue time in orange, planned stages outlined, today in red. Stages without dates are not drawn.",
      },
      closing: {
        title: "Close {{stage}}",
        description:
          "When the stage ended, and what was actually spent on it. Less than planned leaves the rest an estimate; more raises the budget, apportioned as the costs so far.",
        ended: "Ended on",
        costsOf: "Costs of {{name}} at {{stage}}",
        noCosts: "No costs of {{name}} are open at this stage.",
        allAsEstimated: "All as estimated",
        asEstimated: "As estimated",
        column: {
          type: "Cost type",
          planned: "Planned total",
          spent: "Actually spent",
          then: "Then",
        },
        spentOn: "Actually spent on {{type}}",
        asPlanned: "As planned",
        less: "{{amount}} less than planned",
        more: "{{amount}} more than planned",
        invalid: "Enter an amount of 0 or more",
        summary: {
          spent: "Actually spent",
          estimated: "Still estimated",
          notSpent: "Not spent",
          budget: "Budget of the stage",
          raised: "{{amount}} more than planned",
        },
        costTypes,
        future: "A stage cannot end after today",
        today: "Today",
        cancel: "Cancel",
        submit: "Close {{stage}}",
      },
      judgment: {
        title: "Close Trial",
        editTitle: "Edit the judgment",
        description:
          "When judgment was given, what was spent on the stage, and what the judgment decided and awarded. From here on the case stands on the judgment, not on its probabilities.",
        date: "Judgment on",
        estimates: {
          title: "Estimates still open in closed stages",
          description:
            "With the judgment, costs are final: each was spent after all, or will not be.",
          column: {
            party: "Party",
            stage: "Stage",
            type: "Cost type",
            estimated: "Still estimated",
            outcome: "What became of it",
          },
          spent: "Spent",
          notSpent: "Not spent",
        },
        claims: {
          title: "Judgment: claims",
          description:
            "Each is decided per defendant, and a claim with issues issue by issue. Once all are decided, the amounts start from the case's matching outcome.",
          against: "v {{name}}",
          upheld: "Upheld",
          dismissed: "Dismissed",
          undecided: "Undecided",
          column: {
            amount: "Amount",
            claimed: "Claimed",
            awarded: "Awarded",
            then: "Then",
          },
          addInterest: "Interest awarded",
          interest: "Interest",
          awardedFor: "Awarded for {{name}}",
          toEnter: "To enter",
          asOutcome: "As in the outcome",
          lessThanOutcome: "{{amount}} less than the outcome",
          moreThanOutcome: "{{amount}} more than the outcome",
        },
        outcome: {
          chance: "The chance the case gave this outcome",
          hint: "The amounts start from it. Change any the court decided differently.",
          ruledOut:
            "The case ruled this outcome out. The amounts start from what it expected of each claim.",
          loading: "Reading the matching outcome…",
          failed: "The outcome could not be read. Please try again.",
        },
        recoveries: {
          title: "Cost recoveries",
          description:
            "What the winners recover from the losers, per cost type, starting at your estimates. Change any share the court ordered differently.",
          none: "Nobody recovers costs with these decisions.",
          recovers: "{{name}} recovers from {{payers}}",
          and: " and ",
          about: "About {{amount}}",
          toRecover: "To recover: {{amount}}",
          sharesLabel: "Shares",
          shares: { byType: "By cost type", oneShare: "One share" },
          column: {
            type: "Cost type",
            costs: "Costs",
            share: "Share",
            recovery: "Recovery",
            then: "Then",
          },
          shareOf: "Share recovered of {{type}}",
          oneShareOf: "One share of the costs of {{name}}",
          asExpected: "As expected",
          lessThanExpected: "{{amount}} less than expected",
          moreThanExpected: "{{amount}} more than expected",
          subtotal: "Subtotal",
          assessment: {
            title: "Assessment",
            of: "Assessment of",
            costs: "Costs",
            recovery: "Recovery",
            costsHelp: "Costs approved by the court.",
            recoveryHelp: "The final sum the court fixed.",
            assessedCosts: "Assessed costs",
            assessedRecovery: "Assessed recovery",
            edit: "Edit the assessment",
            amount: "Assessed amount",
            save: "Save",
            cancel: "Cancel",
            asSubtotal: "As the subtotal",
            lessThanSubtotal: "{{amount}} less than the subtotal",
            moreThanSubtotal: "{{amount}} more than the subtotal",
          },
          costTypes,
        },
        summary: {
          title: "What the judgment means for {{name}}",
          against: "Awarded against it",
          forIt: "Awarded to it",
          recoveries: "Cost recoveries",
          net: "Net of the judgment",
          netHint: "Before its own costs",
        },
        passedOver:
          "The case moves on to Enforcement, and Appeals is passed over. Closing Trial can be undone by reopening it.",
        missing: "Still needed: {{parts}}",
        parts: {
          date: "the date of judgment",
          costs: "valid amounts of costs",
          estimates: "the open estimates",
          decisions: "a decision on each claim",
          awards: "the awards",
          recoveries: "the cost recoveries",
        },
        future: "A judgment cannot be after today",
        today: "Today",
        cancel: "Cancel",
        submit: "Close Trial",
        save: "Save",
      },
      closingCase: {
        title: "Close Enforcement",
        description:
          "This is the last stage: closing it closes the case. What was spent on the stage, and what was actually paid on each award.",
        date: "Ended on",
        awards: {
          title: "What the judgment awarded",
          description: "What was actually paid on each award.",
          column: {
            award: "Award",
            awarded: "Awarded",
            paid: "Actually paid",
            then: "Then",
          },
          costRecovery: "Cost recovery",
          pays: "{{payer}} pays {{recipient}}",
          paidOn: "Actually paid on {{name}}",
          inFull: "In full",
          toEnter: "To enter",
          paidInFull: "Paid in full",
          notPaid: "{{amount}} not paid",
          notRecovered: "{{amount}} not recovered",
          more: "{{amount}} more than awarded",
          failed: "The awards could not be read. Please try again.",
        },
        summary: {
          title: "What was paid for {{name}}",
          paid: "Paid under the judgment",
          received: "Received under it",
          net: "Net of the judgment",
        },
        reopenHint:
          "Once closed, the case shows these figures in place of its probabilities. Reopening Enforcement takes it back.",
        missing: "Still needed: {{parts}}",
        parts: {
          date: "the end date",
          costs: "valid amounts of costs",
          paid: "what was paid",
        },
        future: "A stage cannot end after today",
        today: "Today",
        cancel: "Cancel",
        submit: "Close Enforcement and the case",
      },
    },
    shownResults: {
      label: "Results shown",
      litigation: "Litigation",
      adjusted: "Settlement-adjusted",
      pending: "The settlement-adjusted results are being calculated:",
      failed: "The settlement-adjusted results could not be calculated.",
      tooLarge:
        "The settlement-adjusted results of this case are too large to store. Fewer chances above 0% give fewer combinations.",
      chances:
        "Settled {{settled}} · won at judgment {{won}} · lost at judgment {{lost}}",
    },
    judged: {
      judgment: "Judgment",
      result: "Judgment: {{result}}",
      upheld: "Upheld",
      dismissed: "Dismissed",
      message:
        "The court has decided this case, so there is no range of outcomes left. Its result is on the outcomes and reserve pages.",
      frozen:
        "A judgment stands on what is on this page. To change it, reopen Trial on the timeline.",
    },
    // what a claim is to the insurer that models it (see ClaimKind)
    claimKind: {
      label: "Kind of claim",
      thirdPartyLiability: "Third-party liability",
      policyholderDispute: "Policyholder dispute",
    },
    title: {
      editDialog: {
        title: "Change case title",
        description: "Please specify the title of the case",
        cancel: "Cancel",
        submit: "Save title",
        tip: {
          title: "Title of the case",
        },
        helper: {
          emptyTitle: "Title must not be empty",
        },
        error: {
          save: "Failed to save case title",
        },
      },
    },
    parties: {
      title: "Parties",
      description:
        "The parties to the case, which of them are your clients, and the assets each could pay from.",
      clientBadge: "Client",
      section: {
        client: "Clients",
        noClient: "Non-clients",
      },
      name: {
        client: "{{name}} (client)",
        noClient: "{{name}}",
      },
      deleteDialog: {
        title: "Delete party?",
        description: "Do you wish to delete party '{{partyName}}'?",
        claims:
          "The {{count}} claims brought by '{{partyName}}' will be deleted as well:",
        claims_one:
          "The claim brought by '{{partyName}}' will be deleted as well:",
        defendant:
          "'{{partyName}}' will also be removed as a defendant from {{count}} claims.",
        defendant_one:
          "'{{partyName}}' will also be removed as a defendant from one claim.",
      },
      tip: {
        add: "Add new party to this case",
        edit: "Edit party",
        delete: "Delete party",
      },
      editDialog: {
        title: "Party editor",
        description:
          "Please specify the party's name. Further, a party can be marked as client.",
        cancel: "Cancel",
        submit: "Save party",
        tip: {
          name: "Name of the party",
          client: "Select if party is your client",
        },
        helperText: {
          emptyName: "Name must not be empty",
        },
        client: "Party is a client",
      },
      assets: {
        addAsset: "Add asset",
        value: "{{value}} in assets",
        unlimited: "Unlimited assets",
        total: "Total assets",
        none: "No assets have been added yet",
        column: {
          asset: "Asset",
          estimates: "Value estimates",
          weightedValue: "Weighted value",
        },
        oneEstimate: "One estimate",
        estimate: "{{value}} at {{share}}",
        deleteDialog: {
          title: "Delete asset?",
          description: "Do you wish to delete the asset '{{name}}'?",
        },
        editDialog: {
          title: "Asset editor",
          description:
            "Please specify a name and a value estimate for the asset. It is possible to specify multiple weighted estimates to reflect different probabilities of the asset having a value.",
          cancel: "Cancel",
          submit: "Save asset",
          tip: {
            name: "Name of the asset",
          },
          helper: {
            emptyName: "Name must not be empty",
            valueMissing:
              "Please use the + button to estimate the value of this asset",
          },
        },
        tip: {
          add: "Add new asset",
          edit: "Edit asset",
          delete: "Delete asset",
          show: "Show assets",
          hide: "Hide assets",
        },

        assetOptions: {
          title: "Assets",
          unlimited: "Unlimited",
          limited: "Limited",
        },
      },
      claimants: "Claimants:",
      add: "Add party",
      wizard: {
        title: "Add new party to the case",
      },
      noParties: "No parties have been added yet",
    },
    recoveries: {
      details: {
        percentage: "of cost awarded",
        type: costTypes,
        description: {
          barristerFee:
            "Please specify the likely % of awarded barrister fee recoveries.",
          courtFee:
            "Please specify the likely % of awarded court fee recoveries.",
          expertFee:
            "Please specify the likely % of awarded expert fee recoveries.",
          lawFirmFee:
            "Please specify the likely % of awarded law firm fee recoveries.",
          otherDisbursement:
            "Please specify the likely % of awarded recoveries for other disbursements.",
        },
        result: "Resulting recovery",
      },
      column: {
        type: "Cost type",
        awarded: "Likely to be awarded",
        rate: "Rate",
      },
      title: "Cost recoveries",
      description:
        "Please specify the likely % of awarded cost recoveries per party.",
      description_zero:
        "Recoveries are defined per party. Please add parties first",
      withVat:
        "The costs include the VAT of {{rate}} that {{name}} cannot reclaim, none on court fees: it recovers the VAT with them.",
      editDialog: {
        title: "Cost recovery editor",
        description:
          "Please specify the payers and recipients of the cost recoveries. Further, please specify what type and % of the awarded recoveries.",
        tip: {},
      },
      goToParties: "Go to parties section",
    },
    apportionments: {
      title: "Cost apportionments",
      section: {
        editor: "Apportionment editor",
        perPartyApportionments: "In the bargaining ranges",
        perPartyLiability: "Liability for costs",
      },
      description:
        "Apportion each party's costs to the opponents they are incurred against. Costs apportioned to no single opponent are generic: every opponent the party wins against is liable for all of them. However many opponents lose, a party recovers no more than 100% of its costs.",
      description_zero:
        "Apportionment is configurable only for parties with two or more opponents",
      party: {
        description:
          "{{name}}'s costs apportionment towards opponents in case.",
        description_one:
          "{{name}}'s costs apportionment towards the opponent in case.",
        description_zero: "{{name}} has no opponents in case.",
      },
      // the per-opponent values of the bargaining ranges split the generic
      // costs equally; what an opponent owes does not (recoveries below)
      results:
        "How {{name}}'s costs count in its bargaining range with each opponent: the share apportioned to it and an equal part of the generic costs.",
      recoveries:
        "The share of {{name}}'s costs each opponent is liable for when {{name}} wins against it: its own share and all of the generic costs. {{name}} recovers no more than 100% of its costs.",
      generic: "Generic costs",
      genericCaption:
        "Apportioned to no single opponent: every opponent {{name}} wins against is liable for all of them",
      perParty: "Towards {{name}}",
      details: {
        percentage: "of costs",
        ofStage: "of the stage",
      },
      // the editor's two modes: the whole budget, or each stage
      mode: {
        budget: "Whole budget",
        stages: "Per stage",
        budgetUnavailable:
          "The stages have different splits: change them per stage.",
      },
      stage: {
        summary: "{{total}}, {{generic}} generic",
      },
      // an opponent whose case a settlement ended
      settled: {
        settledAt:
          "Settled at {{stage}}: what of the costs is towards it is locked",
      },
      error: { updateFailed: "Failed to update apportionment: {{error}}" },
      chart: {
        label: {
          apportionedCosts: "Apportionment",
          genericCosts: "Generic costs",
        },
      },
      goToParties: "Go to parties section",
    },
    claims: {
      claimTypes,
      title: "Claims",
      description:
        "Each claim with its issues, the claimant's win chance against every defendant, and the claimed amounts.",
      number: "Claim {{ordinal}}",
      // a claim named within a sentence
      reference: "claim {{ordinal}} ({{title}})",
      claimant: "Claimant <bold>{{name}}</bold>",
      settled: "Settled",
      kind: {
        specific: "Defendant-specific",
        generic: "Generic",
        label: "Won per defendant or once against all",
        hint: {
          issue: {
            specific:
              "Defendant-specific issues must be won per-defendant. Winning against one of the defendants is independent from winning against others.",
            generic:
              "Generic issues represent general facts, not dependent on individual defendants. They are won once against all liable defendants.",
          },
          claim: {
            specific:
              "Defendant-specific claims must be won per-defendant. Winning against one of the defendants is independent from winning against others.",
            generic:
              "Generic claims represent general facts, not dependent on individual defendants. They are won once against all liable defendants.",
          },
        },
      },
      settledDefendants: {
        all: "Fully settled",
        some: "Partially settled with {{names}}",
        none: "Not settled",
      },
      defendants: "Defendants <bold>{{names}}</bold>",
      defendants_zero: "No defendants configured",
      defendants_one: "Defendant <bold>{{names}}</bold>",
      add: "Add claim",
      winChance: {
        label: "Win chance",
        tooltip: "Claimant's win chance v {{defendant}}",
        tooltipNoDefendant: "Claimant's win chance",
        notInvolved: "Not involved",
      },
      quantums: {
        amountTitle: "Claimed amounts",
        amounts: "Claimed {{claimed}} · award estimate {{awarded}}",
        awardedInJudgment: "Awarded in the judgment: {{amount}}",
        notAwarded: "Not awarded in the judgment",
        interestInJudgment: "Interest in the judgment: {{amount}}",
        value: "{{value}} estimated award value",
        tip: {
          add: "Add claimed amount to claim",
          edit: "Edit claimed amount",
          delete: "Delete claimed amount",
        },
        coverage: {
          all: {
            full: "Winning claim {{claimOrdinal}} against all liable defendants fully covers '{{name}}'",
            partial:
              "Winning claim {{claimOrdinal}} against all liable defendants does not fully cover '{{name}}'",
          },
          any: {
            full: "Winning claim {{claimOrdinal}} against any liable defendant fully covers '{{name}}'",
            partial:
              "Winning claim {{claimOrdinal}} against {{defendants}} does not fully cover '{{name}}'",
          },
        },
        liability: {
          total: "Total liability",
          most: "Most",
          least: "Least",
          editDialog: {
            title: "{{defendant}}'s liability for '{{quantumTitle}}'",
            subTitle: "{{defendant}}'s liability",
            issueLabel: "Issue {{issueOrdinal}}",
            issueDescription: "{{issueTitle}}",
            quantumLabel: "Claimed amount '{{quantumTitle}}'",
            quantumDescription: "Total claimed amount: {{quantumAmount}}",
            hint: "Please specify the monetary amount {{defendant}} is liable for '{{quantumTitle}}'",
            submit: "Save liability",
            cancel: "Cancel",
            sliderLabel: "Liability",
            textFieldLabel: "Liability amount",
          },
          none: "No liability",
          tooltip: "{{defendant}}'s liability for '{{quantumTitle}}' ",
        },
        editDialog: {
          title: "Claimed amount editor",
          sections: {
            name: {
              title: "1. Name",
              label: { name: "Claimed amount name *" },
              description:
                "Name the damage claimed. A damage the claimant claims in other claims can be picked: it is then the same damage, awarded once, at the highest amount among the claims won.",
              // under a damage the claimant claims in other claims
              claimedIn: "Also claimed in {{claims}}",
              helperText: {
                emptyName: "Name must not be empty",
                nameAlreadyUsed: "Claimed amount already defined in claim",
                sameDamage:
                  "The same damage as in {{claims}}: awarded once, at the highest amount among the claims won",
                renamesShared: "Renames it in {{claims}} as well",
                nameOfOtherDamage:
                  "The claimant claims another damage of this name in {{claims}}",
              },
            },
            value: {
              title: "2. Value",
              label: { value: "Claimed amount" },
              description: "Please specify the value of the claimed amount.",
              helperText: {
                valueEmpty: "Value must be provided",
                valueNegative: "Value must not be negative",
              },
            },
            award: {
              title: "3. Award estimate",
              description:
                "Please estimate the claimed amount to be awarded, assuming the claim is successful.",
            },
          },
          cancel: "Cancel",
          submit: "Save claimed amount",
        },
      },
      editDialog: {
        title: "Claim editor",
        description:
          "Please specify a title, the claimant party as well as the defendants of the claim.",
        tip: {
          title: "Claim title",
        },
        helperText: {
          missingTitle: "Title must not be empty",
          missingClaimant: "Claiming party must be selected",
          wrongDefendants: "Defendants must not overlap with claimant",
          missingClaimType: "Claim type must be selected",
        },
        label: {
          claimant: "Claimant",
          defendants: "Defendants",
          noParties: "No parties defined in case",
          claimType: "Claim type",
        },
        cancel: "Cancel",
        submit: "Save claim",
      },
      issues: {
        label: "Issues",
        tip: {
          add: "Add new issue",
        },
        total: "Total",
        againstAll: "Against all",
        againstAny: "Against any",
        menu: {
          label: "Issue actions",
          moveUp: "Move up",
          moveDown: "Move down",
        },
        summary: {
          title: "Issue summary",
          caption: "Claimant's win chance",
          issueWinChance: {
            tooltip: {
              vAny: "Chance of winning issue {{issueOrdinal}} against at least one of the defendants",
              vAll: "Chance of winning issue {{issueOrdinal}} against all defendants",
            },
          },
          claimWinChance: {
            tooltip: {
              vAny: "Chance of winning the claim against at least one of the defendants",
              vAll: "Chance of winning the claim against all defendants",
            },
          },
        },
        header: {
          group: "Grouped issue",
        },
        groups: {
          summary: {
            title: "Group summary",
            caption: "Win chance",
          },
          tip: {
            info: "How this group works",
          },
          infoDialog: {
            title: {
              Or: "'And / Or' issue group",
              Xor: "'Either or' issue group",
            },
            description: {
              Or: "In this <bold>And / Or</bold> group, any number of the sub-issues ({{groupedOrdinals}}) can be won at the same time. However, only one needs to be won for the parent issue {{ordinal}} to be considered won.",
              Xor: "In this <bold>Either or</bold> group, the sub-issues ({{groupedOrdinals}}) are logically exclusive alternatives. One and only one of the sub-issues can be won. Winning one of the sub-issues invalidates the remaining sub-issues of the group.",
            },
            submit: "OK",
          },
        },
        relations: {
          label: "How this issue combines with the next",
          name: {
            And: "And",
            Or: "And / Or",
            Xor: "Either or",
          },
          hint: {
            And: "All issues must be won",
            Or: "One or more issues can be won",
            Xor: "One and only one issue can be won. The win chance sum of 'either or' issues cannot exceed 100%.",
          },
        },
        deleteDialog: {
          title: "Delete issue?",
          description: "Do you wish to delete issue {{issueOrdinal}}?",
        },
        quantums: {
          deleteDialog: {
            title: "Delete claimed amount?",
            description:
              "Do you wish to delete claimed amount '{{name}}' from claim '{{claimTitle}}'?",
          },
        },
      },
      issueDialog: {
        title: "Issue in claim '{{parentClaim}}'",
        parentDefendants: "Same defendants as in claim",
        genericIssue: "Generic issue",
        sections: {
          title: {
            label: "1. Title",
            description: "Please specify a title of the issue.",
          },
          genericIssue: {
            label: "2. Issue type",
            description:
              "Generic issues represent general facts, not dependent on individual defendants. They are won once against all liable defendants. Defendant-specific issues must be won per-defendant. Winning against one of the defendants is independent from winning against others.",
          },
          defendants: {
            label: "3. Defendants",
            description:
              "Issue is claimed by {{claimant}}. Please select the defendants of the issue.",
          },
        },
        tip: {
          title: "Issue title",
          parentDefendants: "Switch off to specify differing defendants",
          genericIssue:
            "Switch between generic and defendant-specific issue type",
        },
        helperText: {
          missingTitle: "Title must not be empty",
        },
        label: {
          defendants: "Defendants",
          noParties: "No parties available as defendants",
        },
        cancel: "Cancel",
        submit: "Save issue",
      },
      deleteDialog: {
        title: "Delete claim?",
        description: "Do you wish to delete the claim '{{claim}}'?",
      },
      tip: {
        add: "Add new claim",
        edit: "Edit claim title and parties",
        delete: "Delete claim",
      },
      noClaims: "No claims have been added yet",
      noClaimsAndNoParties:
        "A claim requires at least two parties. Please add parties first",
      goToParties: "Go to parties section",
      edit: "Edit",
      addIssue: "Add issue",
      addAmount: "Add claimed amount",
    },
    costs: {
      title: "Costs",
      budgeted: "Estimated costs",
      notToBeSpent: "No longer to be spent",
      incurred: "Incurred costs",
      total: "Total costs",
      costTypes,
    },
    prerequisites: {
      title_one: "One more step before this analysis can run",
      title: "{{count}} more steps before this analysis can run",
      intro:
        "The analysis is calculated from the case configuration. Until these are in place the figures would be assumptions rather than results.",
      progress: "{{done}} of {{total}} complete",
      cta: "Go to {{name}}",
      parties: {
        label: "Parties added",
        name: "parties",
        hint: "A claimant and a defendant on the case",
        action: "Add",
      },
      claims: {
        label: "Claim recorded",
        name: "claims",
        hint: "At least one claim, with the defendants it runs against",
        action: "Add",
      },
      amounts: {
        label: "Claimed amounts entered",
        name: "claimed amounts",
        hint: "What the claim is worth if it succeeds",
        action: "Enter",
      },
      winChances: {
        label: "Win chances set",
        name: "win chances",
        hint: "Needed for each side's expected value",
        action: "Set",
        missing_one: "One issue has none",
        missing: "{{count}} issues have none",
      },
      costs: {
        label: "Costs entered",
        name: "costs",
        hint: "Base costs and disbursements",
        action: "Enter",
      },
      feeStructure: {
        label: "Fee structure defined",
        name: "the fee structure",
        hint: "Splits your client fees into costs and profit",
        action: "Define",
      },
      // shown on an issue whose win chance was never entered — the slider reads
      // 0% and an unset defendant reads "Not involved", so neither is evidence
      winChanceMissing: "No win chance entered — treated as a certainty",
      claimAmountMissing:
        "No claimed amount — this claim is worth nothing if it succeeds",
      claimWinChancesMissing_one: "One win chance missing",
      claimWinChancesMissing: "{{count}} win chances missing",
    },
    analytics: {
      name: {
        client: "{{name}} (client)",
        noClient: "{{name}}",
      },
      tree: {
        title: "Decision tree",
        intro:
          "The decision tree of every party of the case: the stages up to the trial, the outcomes the trial can have, and how each of them gets paid. It is generated when you ask for it and is not stored.",
        generate: "Generate decision tree",
        generating: "Generating the decision tree",
        generated: "{{share}} generated",
        drawing:
          "Drawing the decision tree. The page does not respond until it is drawn.",
        tooLarge:
          "The decision tree of this case has {{nodes}} nodes. It can be drawn with up to {{maxNodes}}.",
        busy: "The server is generating the decision tree of another user, and it generates one at a time. Try again in a moment.",
        failed: {
          timeout:
            "Generating the decision tree took too long and was stopped.",
          memory:
            "Generating the decision tree took too much memory and was stopped.",
          error: "The decision tree could not be generated.",
          drawing: "The decision tree was generated but could not be drawn.",
        },
        retry: "Try again",
      },
      ev: {
        chart: {
          expectedValue: "Expected value",
          estimatedCost: "Estimated cost",
          incurredCost: "Incurred cost",
          netResult: "Net result",
          settlement: {
            baselineMidpoint:
              "Bargaining midpoint for {{selectedPartyName}} v {{opponentName}}",
            averageBaselineMidpoint:
              "Average bargaining midpoint against all opponents",
          },
        },
      },
      netResult: {
        title: "Net results",
        netResultTitle: "Average net results per party",
        description:
          "What each party is left with after enforcement: from its worst outcome to its best, with the average between them.",
        ranges:
          "Each bar runs from the party's worst net result to its best; the mark is the average. Select a party to see its figures.",
        onAverage: "{{amount}} on average",
        belowAverage: "{{amount}} below the average",
        aboveAverage: "{{amount}} above the average",
        warning: {
          negative: "Negative average net result for {{name}}",
        },
        label: "Net result",
        noData:
          "Net result analysis cannot be shown until at least one party has outcome",
        average: "Average net result",
        worst: "Worst net result",
        best: "Best net result",
        chart: {
          tooltip: {
            title: "{{name}}'s net result",
            average: "Average: {{value}}",
            averageLabeled: "{{label}}: {{value}}",
            worst: "Worst: {{value}}",
            best: "Best: {{value}}",
          },
        },
      },
      revenue: {
        title: "Revenue",
        intro:
          "What your firm earns from this case, split into indirect costs, direct costs and profit, for the work done and the work still to come.",
        none: "Your firm earns nothing from this case yet: none of its clients has law firm fees or fees of a conditional fee agreement.",
        description: "Your revenue from this case",
        total: "Total",
        margin: "Profit",
        loss: "Loss",
        directCosts: "Direct costs",
        indirectCosts: "Indirect costs",
        incurred: {
          title: "Earned revenue",
          byStage: "Earned revenue by stage",
        },
        estimated: {
          title: "Estimated revenue",
          byStage: "Estimated revenue by stage",
        },
        cfa: {
          fees: "CFA fees",
          successFee: "CFA success fees",
          noWinFee: "CFA no-win fees",
        },
      },
      risk: {
        noOutcomes: "No case outcomes available for {{name}}",
        description: "Case outcomes from {{name}}'s perspective",
        description_one: "Case outcome from {{name}}'s perspective",
        description_zero: "Risk analysis not available, case has no outcomes.",
        title: "Risk analysis",
        intro:
          "A party's outcomes from worst to best, each as wide as it is likely: how much of the probability lies below zero, and how far.",
        chart: {
          enforcementResult: "Enforcement result",
          netResult: "Net result",
          caseValue: "Expected value",
          assetLimit: "Asset limit",
          axis: "Cumulative probability, worst outcome first",
        },
        summary: {
          total: "Total",
          negative: "Negative",
          negativeProbability: "Probability of a negative enforcement result",
          worst: "Worst",
          best: "Best",
        },
        warning: {
          assetLimit:
            "In {{count}} outcomes with the total probability of {{probability}} the assets of {{name}} are depleted",
          assetLimit_one:
            "In one outcome with the probability of {{probability}} the assets of {{name}} are depleted",
        },
      },
      settlement: {
        bargaining: {
          configurationTabs: {
            winChance: "Win chances",
            costs: "Costs",
            assets: "Assets",
          },
          title: "Bargaining simulation",
          claim: "Claim: {{title}}",
          description:
            "Modify the case parameters to simulate and analyze alternative perspectives on the bargaining range.",
          evOfParty: "{{name}}'s expected value",
          midpoint: "Midpoint",
          parameters: "Case parameters",
          baselineOf: "Baseline {{value}}",
          costsOf: "Costs of {{name}}",
          assetsOf: "Assets of {{name}}",
          rangeAt: "Resulting bargaining range at the {{stage}} stage",
          againstBaseline: "against the baseline",
          chart: {
            baseline: "Baseline",
            variant: "Modified variant",
            midOf: "{{label}} midpoint",
            zoneMaximum: "Bargaining range maximum",
            zoneMinimum: "Bargaining range minimum",
            zoneMidpoint: "Bargaining midpoint",
          },
          valueLabels: {
            unchanged: "No change",
            unlimited: "Limit removed",
            limited: "Limit set",
            points: "{{points}} points",
            points_one: "{{points}} point",
          },
          buttons: {
            add: {
              settlement: {
                label: "Add offer",
                tooltip:
                  "Create a settlement offer of {{name}} for {{opponent}}",
              },
            },
            unlimited: {
              assets: {
                label: "Unlimited",
                tooltip: "Assume unlimited assets for {{name}}",
              },
            },
            minus: {
              assets: {
                tooltip: "Reduce {{name}}'s asset value by {{step}}",
              },
              costs: {
                tooltip: "Reduce {{name}}'s costs by {{step}}",
              },
            },
            plus: {
              assets: {
                tooltip: "Increase {{name}}'s asset value by {{step}}",
              },
              costs: {
                tooltip: "Increase {{name}}'s costs by {{step}}",
              },
            },
            reset: {
              assets: {
                tooltip: "Reset {{name}}'s asset value to baseline",
              },
              costs: {
                tooltip: "Reset {{name}}'s costs to baseline",
              },
              winChance: {
                label: "Reset to baseline",
                tooltip: "Reset win chance to baseline",
              },
              all: {
                label: "Reset to baseline",
                tooltip: "Reset all modified values to baseline",
              },
            },
          },
        },
        offerDialog: {
          stages,
          title: "Settlement offer by {{offeror}}",
          description:
            "Offered to {{offeree}}: the sum, the claims it settles and what the sum is paid towards.",
          // what of an offer cannot be changed any more, by its status
          fixed: {
            offered:
              "The sum, the claims and the settlement costs are fixed once the offer is made. Set the offer back to a draft to change them.",
            rejected:
              "This offer is rejected and fixed, with its apportionment. Set it back to a draft to change it.",
            accepted:
              "This offer is accepted and fixed, with its apportionment. Set it back to a draft to change it.",
          },
          sections: {
            payment: {
              offer: {
                title: "Settlement sum",
              },
              costs: {
                title: "Settlement costs",
                description: "What reaching the settlement costs each side.",
              },
            },
            claims: {
              title: "Claims being settled",
              description:
                "The claims between the two parties that the offer settles.",
            },
          },
          claims: {
            // {{claim}} is the claim by its number, as on the Claims page
            name: "{{claim}}: {{title}}",
            option: "{{claim}}: <bold>{{title}}</bold>",
            amount: "{{title}} {{value}}",
            amounts_one: "Claimed amount: {{amounts}}",
            amounts_other: "Claimed amounts: {{amounts}}",
            noAmounts: "No claimed amounts",
            none: "There are no claims between the two parties.",
          },
          costs: {
            offeror: "{{name}}'s settlement costs",
            offeree: "{{name}}'s settlement costs",
            // the button that opens them by cost type
            edit: "Enter {{label}} by cost type",
            save: "Save settlement costs",
            towardsPayer: "Costs towards {{opponent}}",
            settlement: "Settlement costs",
            other: "Remaining costs",
          },
          apportionment: {
            title: "Apportionment",
            hint: {
              draft:
                "What the sum is paid towards, from the top down. Drag a row to change the order.",
              offered:
                "What the sum is paid towards, from the top down. It stays open to change until the offer is accepted or rejected.",
              decided: "What the sum is paid towards, from the top down.",
            },
            settlementAmount: "Settlement sum",
            apportioned: "Apportioned",
            remaining: "Remaining",
            kind: {
              costs: "Costs",
              claimedAmount: "Claimed amount",
            },
            paidOfCap: "{{paid}} / {{cap}}",
            specificAmount: "A specific amount",
            move: "Move {{label}} in the order",
            payTowards: "Pay towards {{label}}",
            claimedIn: "Claimed in {{claims}}",
            mode: {
              label: "How much is paid",
              max: "Max available",
              specific: "Specific amount",
            },
            cap: "Cap",
            availableFunds: "Available funds",
            covered: "Covered",
            notCovered: "{{amount}} not covered",
          },
          warning: {
            notApportioned: "{{amount}} not apportioned",
            exceedsOffer: "Apportionment exceeds the sum by {{amount}}",
          },
          error: {
            noClaimSelected: "Please select at least one claim to settle",
          },
          cancel: "Cancel",
          submit: "Save offer",
          direction: {
            label: "Who pays whom",
            pays: "<bold>{{payer}}</bold> pays {{receiver}}",
          },
          status: {
            draft: "Draft",
            offered: "Offered",
            rejected: "Rejected",
            accepted: "Accepted",
          },
          amount: "Sum",
          amountMidpoint:
            "Bargaining midpoint at {{stage}}: {{payer}} pays {{receiver}} {{amount}}",
          applyMidpoint: "Use it",
        },
        offerSummary: {
          ev: {
            // what the table shows, for those who cannot see it
            label: "Expected values of the offer",
            offer: "Settlement sum {{amount}}",
            floor: "Expected floor v {{name}}",
            mid: "Bargaining midpoint",
            ceiling: "Expected ceiling v {{name}}",
            withoutOffer: "Without settlement",
            delta: "Difference",
            noRange:
              "No bargaining range at this stage — the parties have no expected values against each other.",
          },
          netResult: {
            title: "Net walkaway",
            label: "Net results of the offer",
            offeror: "Net result {{name}}",
            offeree: "Net result {{name}}",
            withOffer: "Offer",
            withoutOffer: "Without settlement",
            delta: "Difference",
          },
          risk: {
            title: "Net result — {{name}}",
          },
          remaining: {
            title: "{{name}} — remaining disputes",
            expected: "Expected outcome (without settlement → settled)",
            takeaway:
              "Settling covers {{covered}} of the {{total}} '{{claim}}' claim, leaving {{remaining}} to pursue against {{opponent}}.",
          },
        },
        netResultChart: {
          noSettlement: "Without settlement",
          withSettlement: "With settlement",
          onlyOneNetResult: "Net walkaway",
        },
        walkaway: {
          title: "Net walkaway",
        },
        deleteOfferDialog: {
          title: "Delete offer?",
          question:
            "Do you wish to delete the settlement offer made by {{offeror}}?",
        },
        acceptDialog: {
          title: "Accept settlement offer",
          description: "On which day was the offer accepted?",
          acceptedOn: "Accepted on",
          future: "An offer cannot be accepted after today",
          today: "Today",
          cancel: "Cancel",
          accept: "Accept offer",
        },
        reopenOfferDialog: {
          title: "Reopen settlement offer?",
          question:
            "This offer was {{status}} and its impact on the case was calculated at the moment of that decision. Reopening it will recalculate the impact from the current state of the case. Continue?",
          status: {
            rejected: "rejected",
            accepted: "accepted",
          },
        },
        settled: {
          title: "This case is fully settled",
          intro:
            "There is no bargaining range left and nothing further to model.",
          settledAt: {
            label: "Settlement sum",
            caption: "agreed by both parties",
          },
          vsMidpoint: {
            label: "Against the midpoint",
            below: "below the bargaining midpoint",
            above: "above the bargaining midpoint",
            at: "at the bargaining midpoint",
          },
          exposure: {
            label: "Worst-case net result improved",
            caption: "compared with the case without settlement",
          },
          viewTerms: "View settlement terms",
          stageLine_one: "Settled at {{stage}}, one stage before trial.",
          stageLine: "Settled at {{stage}}, {{count}} stages before trial.",
          stageLineAtTrial: "Settled at {{stage}}.",
        },
        noRanges:
          "No bargaining ranges can be derived for this case — the parties have no expected values against each other.",
        title: "Settlements",
        intro:
          "The settlement offers a party has made and received, opponent by opponent, against the bargaining range: the range the two sides could rationally agree in.",
        summary: "Settlement summary",
        description: "Settlement offers made and received by {{name}}.",
        titleSettlementChart: "{{name}} v {{other}}",
        counts: {
          total: "Offers total",
          pending: "Offered",
          accepted: "Accepted",
          rejected: "Rejected",
          drafts: "Draft",
        },
        // where a party stands with an opponent, or on one claim with it
        standing: {
          title: "Where it stands with each opponent",
          settled: "Fully settled",
          partly: "Partially settled",
          pending: "Offered",
          rejected: "Offer rejected",
          draft: "Draft",
          none: "No offer",
          claims: "<bold>{{settled}} of {{count}} claims</bold> settled",
          claims_one: "<bold>{{settled}} of {{count}} claim</bold> settled",
          agreed: "{{amount}} agreed at {{stage}}",
          offered: "{{amount}} offered at {{stage}}, not answered yet",
          refused: "{{amount}} rejected at {{stage}}",
          drafted: "{{amount}} in a draft for {{stage}}",
          noOffers: "No offers yet",
        },
        progress: {
          title: "How far each claim is settled",
          description:
            "What the accepted settlements pay towards each claimed amount, and what is on offer.",
          settled: "Settled",
          offered: "On offer",
          open: "Open",
          settledWith: "Settled with {{settled}} of {{count}} opponents",
          settledWith_one: "Settled with {{settled}} of {{count}} opponent",
          claimed: "{{amount}} claimed",
          settledOf: "<bold>{{amount}}</bold> settled ({{share}})",
          paidBy: "{{name}} {{amount}}",
          onOfferWith: "on offer with {{name}} {{amount}}",
          uncovered: "No offer covers it",
          settledTip: "Settled: {{amount}}",
          offeredTip: "On offer: {{amount}}",
        },
        // a claim or a claimed amount named as what it is: a bare title such
        // as "Lost profits" reads like a figure of the firm
        named: "<quiet>{{label}}:</quiet> <bold>{{title}}</bold>",
        claimedAmount: "Claimed amount",
        offers: "{{count}} offers",
        offers_one: "{{count}} offer",
        prospects: {
          stage: "Stage",
          chance: "If it gets here: chance of settling",
          chanceOf: "Chance of settling at {{stage}}, if the case gets there",
          settlesAt: "Settles at",
          settlesHere: "Settles here",
          stillInDispute: "Still in dispute after",
          closed: "Closed",
          current: "Current stage",
          resulting: "Resulting chances",
          ofSettling: "Of settling",
          litigation: "Litigation",
          rangeTitle: "Where in the bargaining range",
          worstFor: "{{share}}: worst for {{name}}",
          midpoint: "Midpoint",
          create: "Create offer",
          noRange: "No bargaining range at this stage.",
          afterJudgment:
            "Settling after the judgment is not calculated yet: it would depend on the judgment.",
          noRangeHere:
            "No range at this stage: the two have nothing to agree on, so it takes no chance.",
          outsideRange:
            "This offer is outside the bargaining range at {{stage}}: {{name}} would do better by litigating. Set the chance to 0% if you do not expect a settlement at this amount.",
          ifFirst: "If {{name}} settles first",
          ifFirstNote:
            "This offer then no longer applies. Where would {{party}} and {{opponent}} settle instead?",
          litigated: "{{name}}'s expected value, litigated",
          gained: "Gained by settling, on average",
          adjusted: "Settlement-adjusted expected value",
          pending: "The settlement-adjusted results are being calculated:",
          failed: "The settlement-adjusted results could not be calculated.",
          tooLarge:
            "The settlement-adjusted results of this case are too large to store. Fewer chances above 0% give fewer combinations.",
        },
        timeline: {
          offersBy: "Offers by {{name}}",
          to: "to {{name}}",
          closed: "Settled at {{stage}} for {{amount}}",
        },
        card: {
          acceptedOn: "on {{date}}",
          offerBy: "Offer by {{name}}",
          paidBy: "to be paid by {{name}}",
          settles: "What the offer settles",
          noClaims: "The offer settles no claim yet",
          paidOf: "<bold>{{paid}}</bold> of {{claimed}}",
          notApportioned: "Not fully apportioned",
          netResult: "{{name}}'s expected net result",
          without: "Without the offer",
          with: "With the offer",
          results: "Results",
          edit: "Edit offer",
          delete: "Delete offer",
          status: "Status of the offer: {{status}}. Change",
          statusMenu: "Status of the offer",
        },
        range: {
          title: "The offers against the bargaining range",
          valuesAt: "Settlement values at {{stage}}",
          caseValue: "{{name}}'s expected value",
          inverseCaseValue: "{{name}}'s inverse expected value",
          caption:
            "The numbered points are the offers of the timeline, in the same colours, each at the stage and the amount it was made. Select a stage to see its values.",
          offer: "Offer {{number}}, {{status}}",
          show: "Show the bargaining range",
          hide: "Hide the bargaining range",
        },
        noOutcomes:
          "Settlement analysis not available, case has no outcomes yet",
        noData: "No settlement data available",
        chart: {
          labels: {
            evSettlingParty:
              "{{settlingParty}}'s expected value v {{otherParty}}",
            evOther:
              "{{otherParty}}'s inverse expected value v {{settlingParty}}",
            midpoint: "Midpoint",
            netResult: "Net result without settlement",
            worstEnforcement: "Worst-case enforcement for {{settlingParty}}",
            bestEnforcement: "Best-case enforcement for {{settlingParty}}",
          },
        },
        noOpponents:
          "This party has no opponents to analyze settlement data with",
      },
      sensitivity: {
        description:
          "Following charts simulate the impact of variable changes on the expected value, from {{name}}'s perspective.",
        variables: {
          claimWinChance: "Win chance for {{claim}}",
          costs: "Costs of {{name}}",
          assets: "Assets of {{name}}",
          ev: "Expected value",
        },
      },
      outcomes: {
        title: "Case outcomes",
        intro:
          "Every way the case can end from one party's perspective, grouped by what the party is left with.",
        description: "Case outcomes from {{name}}'s perspective.",
        description_zero: "Case has no outcomes.",
        detailsError: "Failed to load outcome details.",
        probability: "Probability: {{probability}}",
        column: {
          probability: "Probability",
          outcomes: "Outcomes",
        },
        details: "Details",
        detailsOf: "Details of the outcomes with a net result of {{amount}}",
        settlement: { row: "Settlement", stages: "Stages" },
        judgments: {
          description:
            "Possible case outcomes, grouped and sorted by their net result, from lowest to highest.",
          number: "{{numberOfJudgments}} outcomes",
          number_one: "Single outcome",
        },
        summary: {
          title: "Outcome summary",
          judgments: {
            total: "Outcomes",
            negative: "With a negative judgment balance",
            negativeProbability: "Probability of a negative judgment balance",
          },
          outcomes: {
            total: "Distinct net results",
            negative: "Outcomes with a negative net result",
            negativeProbability: "Probability of a negative net result",
          },
          alerts: {
            allNegative: "Every net result of {{name}} is negative",
            mostNegative:
              "{{name}} has a {{probability}} chance of a negative net result",
          },
        },
        chart: {
          netResults: {
            title: "Outcomes by net result",
            probability: "{{probability}} probability",
          },
        },
        netResult: "Net result: {{amount}}",
        warning: {
          debt: "{{value}} debt after enforcement",
          enforcementValue: "{{value}} not enforceable",
        },
        outcomeDialog: {
          settlementStatus: {
            none: "Not settled",
            full: "Fully settled",
            partial:
              "Partially settled: claims '{{claims}}' with {{opponents}}",
          },
        },
      },
    },
    details: {
      section: {
        overview: "Overview",
        configuration: {
          parties: "Parties",
          timeline: "Timeline",
          claims: "Claims",
          costs: "Costs",
          agreements: "Agreements",
          costStructure: "Fee structure",
          apportionments: "Apportionment",
          settlement: "Settlements",
          recoveries: "Recoveries",
        },
        analytics: {
          value: "Expected value",
          netResults: "Net results",
          risk: "Risk analysis",
          bargaining: "Bargaining",
          sensitivity: "Sensitivity",
          revenue: "Revenue",
          outcomes: "Case outcomes",
          tree: "Decision tree",
          var: "Value at risk",
          reserve: "Reserve",
        },
      },
      overview: {
        lawFirm: {
          title: "Law Firm",
        },
        costProgress: {
          title: "Cost progress",
        },
        agreements: {
          title: "Agreements",
        },
        winChances: {
          title: "Win chances",
        },
        settlement: {
          title: "Settlement",
        },
        profit: {
          title: "Earned profit",
        },
      },
      caseId: "Case '{{caseId}}'",
      createdAt: "Created: {{date}}",
      updatedAt: "Last update: {{date}}",
      label: {
        sections: "Case sections",
        configuration: "Configuration",
        analytics: "Analytics",
      },
      delete: "Delete case",
    },
    list: {
      card: {
        createdAt: "Created: {{date}}",
        updatedAt: "Last update: {{date}}",
      },
      importDialog: {
        title: "Import case",
        selectFile: "Select a case file to import.",
        chooseFile: "Choose file",
        invalidFile: "This file is not a valid CaseSolv file.",
        passwordIntro: "Enter the password for '{{name}}'.",
        password: "Password",
        decrypt: "Continue",
        wrongPassword: "Wrong password, or the file is corrupted.",
        unsupportedVersion:
          "This case file was created by a newer version of the app and cannot be imported.",
        existsQuestion:
          "A case with this identifier already exists. Overwrite the existing case, or import as a separate copy?",
        existsReadOnlyQuestion:
          "A case with this identifier already exists. You can view that case and cannot overwrite it. Import as a separate copy?",
        overwrite: "Overwrite",
        copy: "Create copy",
        copyName: "{{name}} (Copy)",
        cancel: "Cancel",
        import: "Import",
        error: "Failed to import case.",
      },
      deleteDialog: {
        title: "Delete case '{{id}}'?",
        question: "Do you wish to irreversibly delete case '{{name}}'?",
        delete: "Delete",
        cancel: "Cancel",
      },
    },
  },
  general: {
    dataAtoms: {
      total: "Total",
    },
    expand: "Expand",
    collapse: "Collapse",
  },
  login: {
    title: "Sign in",
    submit: "Sign in",
    sessionExpired: "Your session has expired. Please sign in again.",
    label: {
      email: "Username or email",
      password: "Password",
      remember: "Remember me",
    },
    error: {
      signInFailed: "Sign-in failed",
      // the server knows no user with this username or email and password
      invalidCredentials: "Check your username or email and your password.",
      // any other failure, such as a server that cannot be reached
      tryAgain: "Try again in a moment.",
    },
    mfa: {
      title: "Two-factor authentication",
      tokenIntro:
        "Enter the 6-digit verification code from your authenticator app.",
      recoveryIntro:
        "Enter one of your recovery codes. Each code can be used only once.",
      tokenLabel: "Verification code",
      recoveryLabel: "Recovery code",
      submit: "Verify",
      back: "Back to sign in",
      useRecovery: "Use a recovery code",
      useToken: "Use a verification code",
      error: {
        title: "Verification failed",
        invalidToken: "Invalid verification code",
        invalidRecoveryCode: "Invalid recovery code",
      },
    },
    quotes: [
      {
        text: "Absolutely a business case for this",
        author: "Insurance professional",
      },
      {
        text: "The software will force fee earners to think early on about the case issues",
        author: "Commercial litigator",
      },
      {
        text: "Modelling a firm's book of cases is invaluable",
        author: "Commercial litigator",
      },
      {
        text: "Incredibly powerful",
        author: "Insurance litigator",
      },
      {
        text: "I’ve never seen anything like this",
        author: "Commercial litigator",
      },
      {
        text: "It really is an outstanding product",
        author: "Commercial litigator",
      },
      {
        text: "Really easy to use",
        author: "Litigation funder",
      },
      {
        text: "Entering the data is quick and intuitive",
        author: "Commercial litigator",
      },
      {
        text: "A really valuable tool",
        author: "Insurance professional",
      },
      {
        text: "Remarkable how much is in here",
        author: "Chief Transformation Officer, international law firm",
      },
    ],
  },
  loading: {
    headline: "Loading…",
  },
  mfa: {
    enrolment: {
      title: "Set up two-factor authentication",
      intro:
        "Scan the QR code below with an authenticator app (for example Google Authenticator, Microsoft Authenticator, 1Password or Authy), then enter the 6-digit code the app shows to confirm.",
      manualSecretLabel:
        "Can't scan the code? Enter this secret manually in your authenticator app:",
      tokenLabel: "Verification code",
      confirm: "Confirm and enable",
      logout: "Sign out",
      error: {
        title: "Could not enable two-factor authentication",
        confirmFailed:
          "The code did not match. Please try again with a freshly generated code from your authenticator app.",
      },
      recovery: {
        warningTitle: "Save your recovery codes now",
        warningBody:
          "Store these codes in a safe place. Each code lets you sign in once if you lose access to your authenticator. We cannot show these codes again.",
        copy: "Copy codes",
        acknowledge: "I have saved my recovery codes",
        continue: "Continue to the application",
      },
    },
    reset: {
      title: "Reset two-factor authentication",
      description:
        "Enter a code from your authenticator app, or one of your recovery codes, to confirm.",
      warning:
        "Your authenticator app and your recovery codes stop working. You set up two-factor authentication again before you can carry on.",
      tokenLabel: "Verification code or recovery code",
      cancel: "Cancel",
      confirm: "Reset",
      error:
        "Reset failed. Check the code and try again, or use a recovery code.",
      errorTitle: "Reset failed",
    },
  },
  appBar: {
    title: "CaseSolv",
    home: "Home",
    breadcrumb: "Breadcrumb",
    readOnly: "Read only",
    readOnlyTip: "You can view this case. You cannot change or delete it.",
    persona: {
      insurer: "Insurer",
    },
    logout: "Sign out",
    anonymousUser: "User",
    userProfile: {
      title: "User profile",
      description:
        "Your account, the currency your amounts are shown in, and how you sign in.",
      account: {
        title: "Account",
      },
      username: "Username",
      email: "Email",
      role: {
        title: "Role",
        description:
          "The role you work in: it sets the terms, menus and figures to suit how you look at a case. Your administrator gives you your roles.",
        lawFirm: "Law firm",
        insurer: "Insurer",
      },
      currency: {
        title: "Currency",
        description:
          "The currency your amounts are shown in. Changing it changes the symbol only: amounts are not converted.",
        option: "{{symbol}} {{code}}",
        EUR: "EUR",
        GBP: "GBP",
        USD: "USD",
      },
      security: {
        title: "Security",
        signIn: {
          withCode:
            "You sign in with your username or email, your password and a code from your authenticator app.",
          passwordOnly:
            "You sign in with your username or email and your password.",
        },
        password: {
          title: "Password",
          description:
            "Changing it signs you out in every other browser and on every other device.",
          change: "Change password",
          changed:
            "Your password is changed. You are signed out in every other browser and on every other device.",
          dialog: {
            title: "Change password",
            description: "Enter your current password, then the new one twice.",
            current: "Current password",
            new: "New password",
            repeat: "Repeat the new password",
            rule: "At least {{min}} characters",
            ruleMissing: "At least {{min}} characters: {{missing}} more",
            mismatch: "The two entries are not the same.",
            currentWrong:
              "This is not your current password. Your password was not changed.",
            failed: "Your password could not be changed. Try again.",
            note: "You stay signed in here. Every other browser and device is signed out.",
            cancel: "Cancel",
            confirm: "Change password",
          },
        },
        mfa: {
          label: "Two-factor authentication",
          enabled: "Enabled",
          notRequired: "Not required",
          setUp: "Set up on {{date}}.",
          resetting:
            "Resetting it removes your authenticator app from the account, and you set one up again straight away.",
          exempt: "Your account is exempt from two-factor authentication.",
          reenrol: "Reset",
        },
      },
    },
  },

  weightedValue: {
    label: {
      value: "Value",
      weight: "Weight | Probability",
      result: "Resulting average value",
      button: {
        add: "Add value estimate",
      },
    },
    tip: {
      button: {
        delete: "Delete value estimate",
        add: "Add new value estimate",
      },
      share: "Resulting probability of value estimate",
      weight: "Weight of this value in relation to other estimates",
    },
  },
  weightedAwards: {
    label: {
      award: "Award {{index}}",
      value: "Award estimate",
      weight: "Weight | Probability",
      result: "Resulting award estimate",
      percentage: "{{percentage}} of claim",
      button: {
        add: "Add award estimate",
      },
    },
    tip: {
      button: {
        delete: "Delete award estimate",
        add: "Add new award estimate",
      },
      share: "Resulting probability of award estimate",
      weight: "Weight of this award in relation to other estimates",
    },
  },
  dialog: {
    close: "Close",
    yesNo: {
      yes: "Yes",
      no: "No",
    },
  },
  passwordField: {
    show: "Show what is entered as '{{field}}'",
    hide: "Hide what is entered as '{{field}}'",
  },
  moreMenu: {
    tip: {
      edit: "Edit",
      delete: "Delete",
    },
  },

  charts: {
    tooltip: {
      labelledValue: "{{label}}: {{value}}",
    },
  },
  tornado: {
    tooltip: {
      increase:
        "{{variable}} increased by {{delta}} to {{value}} (+{{percent}})",
      decrease:
        "{{variable}} decreased by {{delta}} to {{value}} (-{{percent}})",
    },
  },
  error: {
    header: "Oops!",
    message: "Sorry, an unexpected error has occurred.",
    status: "Status {{status}}: {{text}}",
  },
  portfolio: {
    results: {
      label: "Results shown",
      litigation: "Litigation",
      adjusted: "Settlement-adjusted",
    },
    // under a profit figure: the cases it leaves out (their revenue counts)
    withoutFeeStructure_one: "Leaves out one case that has no fee structure",
    withoutFeeStructure:
      "Leaves out {{count}} cases that have no fee structure",
    noCases: "No cases defined, the portfolio is empty.",
    noClients: "No clients found in portfolio.",
    noFilterMatches: "No cases matching filter criteria found",
    winChances: {
      title: "Win chances",
      winChanceSummary: "Win chance stats",
      histogram: {
        title: "Chances of winning claims",
        description:
          "Distribution of your clients' chances for a non-negative judgment balance: the balance of amounts awarded and to pay, as ruled by the judge.",
        label: "Clients' cases",
        caption:
          "Win chance, with the number of clients' cases in each range of five points",
        tooltip: {
          description: "{{count}} cases with a win chance of {{range}}",
          description_one: "One case with a win chance of {{range}}",
        },
      },
    },
    caseValues: {
      title: "Expected values",
    },
    evs: {
      evSummary: "Expected value stats",
      cards: {
        total: {
          title: "Total expected value",
          subTitle: "of all cases",
        },
        average: {
          title: "Average expected value",
          subTitle: "per client",
        },
        min: {
          title: "Lowest expected value",
          subTitle: "{{partyName}} in '{{caseName}}'",
        },
        max: {
          title: "Highest expected value",
          subTitle: "{{partyName}} in '{{caseName}}'",
        },
      },
    },
    netResults: {
      netResultSummary: "Net result stats",
      cards: {
        total: {
          title: "Total net result",
          subTitle: "of all cases",
        },
        average: {
          title: "Average net result",
          subTitle: "per client",
        },
        min: {
          title: "Lowest net result",
          subTitle: "{{partyName}} in '{{caseName}}'",
        },
        max: {
          title: "Highest net result",
          subTitle: "{{partyName}} in '{{caseName}}'",
        },
      },
    },
    valueChart: {
      title: "Expected results across client cases",
      shown: "Result shown",
      ev: "Expected value",
      netResult: "Net result",
      caption: {
        ev: "Each client's case, from the lowest expected value to the highest. Select a bar to open its case.",
        netResult:
          "Each client's case, from the lowest net result to the highest. Select a bar to open its case.",
      },
    },
    settlement: {
      title: {
        none: "Your clients reached no settlements",
        // each of the three counts has a plural of its own, so the sentence
        // nests them
        summary:
          'Your clients reached $t(portfolio.settlement.title.settlements, {"count": {{total}} }) to settle $t(portfolio.settlement.title.claims, {"count": {{claims}} }) with $t(portfolio.settlement.title.opponents, {"count": {{opponents}} })',
        settlements_one: "{{count}} settlement",
        settlements_other: "{{count}} settlements",
        claims_one: "{{count}} claim",
        claims_other: "{{count}} claims",
        opponents_one: "{{count}} opponent",
        opponents_other: "{{count}} opponents",
      },
      emptyHint:
        "Once cases settle, this area shows how good the settlements were, what cost they saved and how much they improved the worst-case net result.",
      overview: {
        title: "Settlement overview",
        description:
          "Settlement volume, and how much settling improved the worst-case net result. It compares each case as it resolved against the same case without settlement (every offer rejected): a whole-case comparison, because settlements within a case interact and per-offer figures do not add up.",
        settledCases: {
          title: "Settled cases",
          // stands behind the number of the settled cases
          ofTotal: "of {{total}}",
          subTitle: "{{rate}} of the portfolio",
        },
        // the average is per client per case, not per settlement: a client's
        // settlements within one case are summed into a single figure
        received: {
          title: "Received by clients",
          subTitle: "{{avg}} on average per case",
        },
        paid: {
          title: "Paid by clients",
          subTitle: "{{avg}} on average per case",
        },
        // per case, not per settlement: settlements on one case interact, so
        // the whole-case comparison is the honest total (see the description)
        exposure: {
          title: "Worst-case net result improved",
          subTitle: "{{avg}} on average per case",
          noBaseline: "Not yet compared with the case without settlement",
        },
      },
      // the section heading that groups risk-vs-reward, bargaining analysis and
      // the change per settled case
      sectionTitle: "Settlement quality",
      // charts that compare against the case without settlement have nothing to
      // show for cases settled before those figures were recorded
      noBaseline:
        "No settled case in this view has figures without settlement to compare against yet. Cases settled before this analysis was added get them the next time they are recalculated.",
      // y-axis title of the scatters that place their dots by claim value
      // (bargaining analysis, change per settled case)
      claimAxis: "Claim value →",
      quality: {
        title: "Bargaining analysis",
        empty:
          "No settlement in this view had a usable range: the two sides' expected values coincided, so there was no midpoint to measure against.",
        description:
          "Every settlement is one dot, placed by where the agreed figure landed between the two sides' rational positions: the client's own expected value is the least it should accept, the opponent's the most it could squeeze them to. Further right is better: below 0% the client settled for less than was expected without settlement, above 100% for more than the opponent's own best case. Higher dots are bigger claims.",
        chartTitle: "Where the clients settled in the bargaining range",
        chartSubtitle:
          "Between the client's expected value (0%) and the opponent's (100%)",
        // what the plot shows, for those who cannot see it
        chartLabel:
          "The settlements by where they landed in the bargaining range and by claim value",
        median: "Median",
        average: "Average",
        ourEv: "Client's EV",
        evenSplit: "Even split",
        opponentEv: "Opponent's EV",
        belowFloor: "{{count}} below the client's EV",
        conceded: "{{count}} conceded",
        beatSplit: "{{count}} beat the split",
        // the other side in a sentence of the tooltip when its record does not
        // name it
        opponentFallback: "opponent",
        settledBelowClientEv: "{{amount}} below client's expected value",
        settledAboveOpponentRange:
          "{{amount}} above {{opponent}}'s bargaining range",
        settledAboveMid: "{{amount}} above the bargaining midpoint of {{mid}}",
        settledBelowMid: "{{amount}} below the bargaining midpoint of {{mid}}",
        settledAtMid: "At the bargaining midpoint",
        // the three figures of the tooltip; an opponent its record does not
        // name is "Opponent's EV", as over its line of the chart
        settledFor: "Settled for",
        clientEv: "Client's EV",
        namedOpponentEv: "{{opponent}}'s EV",
        stripRange: "Bargaining range",
        stripLabel: "Bargaining range and the settled figure",
      },
      riskReward: {
        title: "Risk vs. reward",
        description:
          "Each settled case placed by what settling traded: horizontally, how much it improved the worst-case net result; vertically, how much it changed the expected net result. The dashed lines mark no change: top right is better on both counts, bottom right traded expected net result for a better worst case (usually rational), bottom left is worse on both, top left kept more expected net result at a worse worst case. A case unchanged on one count is judged by the other; one unchanged on both is counted separately.",
        chartTitle: "What settling changed",
        chartSubtitle:
          "Worst-case net result improvement vs. change in expected net result",
        chartLabel:
          "The settled cases by how much settling improved their worst-case net result and changed their expected net result",
        xAxis: "Worst-case net result improved →",
        yAxis: "Change in expected net result →",
        // a case unchanged on one count is in the first or the fourth group, so
        // neither may claim a change on both
        betterBoth: "{{count}} better, nothing given up",
        trade: "{{count}} better worst case, lower expected net result",
        gamble: "{{count}} higher expected net result, worse worst case",
        dominated: "{{count}} worse, nothing gained",
        unchanged: "{{count}} unchanged",
        evBetter: "Expected net result {{amount}} higher",
        evWorse: "Expected net result {{amount}} lower",
        evSame: "Expected net result unchanged",
        trialBetter:
          "Case settled {{amount}} better than the worst-case net result without settlement",
        trialWorse:
          "Case settled {{amount}} worse than the worst-case net result without settlement",
        trialSame:
          "Case settled with the same result as the worst-case net result without settlement",
        trialProbability: "which had a probability of {{probability}}",
        worstBetter:
          "The worst-case net result is {{amount}} better than without settlement",
        worstWorse:
          "The worst-case net result is {{amount}} worse than without settlement",
        worstSame:
          "The worst-case net result is the same as without settlement",
      },
      settledVsLitigated: {
        title: "Change per settled case",
        description:
          "One dot per settled case, placed by how much settling changed the outcome against the same case without settlement. Higher dots are bigger claims; extreme differences are compressed into the edges so the bulk stays readable.",
        // names the control that switches between the two metrics
        toggleLabel: "Difference the cases are placed by",
        // shared by both metrics
        medianChange: "Median change",
        avgChange: "Average change",
        noChange: "no change",
        tooltipClaim: "Claim {{claim}}",
        // each caption stands in a narrow column, "\n" breaks it into lines
        stripBefore: "Without\nsettlement",
        stripAfter: "Settled",
        stripLabel: "Net result range without and with settlement",
        // the two views, and the wording each needs
        netResult: {
          toggle: "Expected net result",
          chartTitle: "How much settling changed the expected net result",
          chartSubtitle:
            "Right = settling raised the expected net result · left = lowered it",
          chartLabel:
            "The settled cases by how much settling changed their expected net result and by claim value",
          deltaBetter: "{{amount}} better than without settlement",
          deltaWorse: "{{amount}} worse than without settlement",
          deltaSame: "Same as without settlement",
          improved: "{{count}} better than without settlement",
          unchanged: "{{count}} same as without settlement",
          reduced: "{{count}} below it",
        },
        exposure: {
          toggle: "Worst-case net result",
          chartTitle: "How much settling improved the worst-case net result",
          chartSubtitle:
            "Right = settling improved the worst-case net result · left = worsened it",
          chartLabel:
            "The settled cases by how much settling improved their worst-case net result, and by claim value",
          deltaBetter: "Worst-case net result {{amount}} better",
          deltaWorse: "Worst-case net result {{amount}} worse",
          deltaSame: "Worst-case net result unchanged",
          improved: "{{count}} with a better worst case",
          unchanged: "{{count}} unchanged",
          reduced: "{{count}} with a worse worst case",
        },
      },
      savings: {
        title: "Cost saved by settling",
      },
      costSaved: {
        empty:
          "Settling avoided no litigation cost in this view: the costs are the same with and without settlement.",
        title: "Litigation cost saved",
        description:
          "The difference in litigation cost between these cases as they resolved and the same cases without settlement, across every cost type. The total is the sum of the breakdown below it.",
        total: {
          title: "Total cost avoided",
          subTitle: "across all settled cases",
        },
        avg: {
          title: "Average per case",
          subTitle: "cost avoided per settled case",
        },
        byType: {
          title: "Cost avoided by type",
          description:
            "What the clients did not have to spend because these cases settled. The law firm fees are the firm's missed revenue; the rest are pass-through costs (barrister, court, expert) the clients simply avoided.",
        },
      },
      costType: {
        lawFirmFee: "Law firm fees",
        barristerFee: "Barrister fees",
        courtFee: "Court fees",
        expertFee: "Expert fees",
        otherDisbursement: "Other disbursements",
        vat: "VAT",
      },
      comparison: {
        title: "Settling vs. not settling",
        settled: "Settled",
        litigated: "Without settlement",
        // the difference line under a pair, `amount` being the formatted gap
        difference: {
          better: "{{amount}} better than without settlement",
          worse: "{{amount}} worse than without settlement",
        },
        clientTitle: "Clients",
        clientDescription:
          "Your clients' combined position with their settlements against the same cases without settlement.",
        // follows the description of the first card that has pairs of bars
        ownScale: "Each pair of bars has a scale of its own.",
        clientNet: "Expected net result",
        clientExposure: "Worst-case net result",
        firmTitle: "Law firm",
        firmDescription:
          "The firm's own economics. Settling usually forgoes future billings, except under a CFA, where a settlement can still trigger the success fee.",
        firmMargin: "Profit",
        firmRevenue: "Revenue",
        firm: {
          helped:
            "Settling earned the firm {{amount}} more than it would have earned without settlement.",
          hurt: "Settling cost the firm {{amount}} in billings it would have earned without settlement.",
        },
      },
    },
    quantum: {
      title: "Claimed amounts",
      byClients: {
        title: "Claimed amounts by clients",
        who: "{{partyName}} in '{{caseName}}'",
      },
      againstClients: {
        title: "Claimed amounts against clients",
        who: "against {{partyName}} in '{{caseName}}'",
      },
      cards: {
        average: {
          title: "Average claimed amounts per case",
          subTitle_one: "across one case",
          subTitle_other: "across {{count}} cases",
        },
        averageAmount: {
          title: "Average claimed amount",
          subTitle_one: "across one claimed amount",
          subTitle_other: "across {{count}} claimed amounts",
        },
        max: {
          title: "Highest claimed amounts",
        },
        min: {
          title: "Lowest claimed amounts",
        },
      },
    },
    analytics: {
      mainTitle: "Portfolio analytics",
      title:
        "You work for {{numberOfClients}} clients in {{numberOfCases}} cases",
      cards: {
        average: {
          title: "Average win chance",
          subTitle_one: "in one case",
          subTitle_other: "in {{count}} cases",
        },
        min: {
          title: "Lowest win chance",
          subTitle: "{{partyName}} in '{{caseName}}'",
        },
        max: {
          title: "Highest win chance",
          subTitle: "{{partyName}} in '{{caseName}}'",
        },
      },
    },
    var: {
      title: "Value at risk",
      description: "Estimated value at risk across your client portfolio.",
    },
    section: {
      clients: "Clients",
      firm: "Firm",
      claims: "Claims",
      settlements: "Settlements",
    },
    // an insurer's page of its claims (see ReservePortfolio)
    claims: {
      none: "No claim has a reserve yet: reserves are worked out when a claim is calculated.",
      allClaims: "All claims",
      columns: {
        group: "Group",
        head: "Head",
        claims: "Claims",
        paid: "Paid",
        outstanding: "Outstanding",
        incurred: "Incurred",
        perClaim: "Average per claim",
        indemnityPerClaim: "Average indemnity",
        defenceToIndemnity: "Defence to indemnity",
      },
      reserves: {
        title: "Reserves",
        totals: "Reserve totals",
        incurredCaption: "Paid and outstanding",
        across_one: "Across one claim",
        across_other: "Across {{count}} claims",
      },
      classes: {
        title: "Classes of business",
        byClass: "Cost per claim by class",
        note: "A claim of several classes counts for each.",
        byKind: "Cost per claim by kind of claim",
      },
      range: {
        title: "Range of reserves",
        outstanding: "Outstanding across all claims",
        description:
          "The outstanding of all claims together, simulated 10,000 times from the outcomes of each: the mean, and what it stays below with each chance.",
        mean: "Mean (indicated)",
        chances: ["Median", "P75", "P90", "P99.5"],
        none: "No claim has outcomes to simulate.",
      },
      largest: {
        title: "Largest claims",
        claim: "Claim",
        kind: "Kind",
      },
    },
    subsection: {
      reserves: "Reserves",
      classes: "Classes of business",
      range: "Range of reserves",
      largest: "Largest claims",
      revenue: "Revenue",
      margin: "Profit",
      netResult: "Expected profit",
      var: "Value at risk",
      winChances: "Win chances",
      caseValues: "Expected values",
      quantums: "Claimed amounts",
      overview: "Overview",
      quality: "Quality",
      settledVsLitigated: "Change per settled case",
      savings: "Cost saved",
      comparison: "Settling vs. not settling",
    },
    // what each page of the portfolio is about, under its title
    description: {
      claims:
        "What your claims will cost: paid, outstanding and incurred, by class of business, and the range of reserves across the portfolio.",
      firm: "What your firm makes of its cases: the profit it expects from each, and its revenue and profit, earned so far and estimated.",
      clients:
        "What the cases are worth to your clients: their expected values and net results, the claimed amounts, the chances of winning, and the value at risk across the portfolio.",
      settlements:
        "How good your clients' settlements were, what cost they saved and how much they improved the worst-case net result, against the same cases without settlement.",
    },
    navigation: "Portfolio sections",
    caseCounter: "<bold>{{current}}</bold> of {{total}} cases",
    caseCounter_one: "<bold>{{current}}</bold> of 1 case",
  },
  outcomeDialog: {
    title: "Net result",
    group: "{{count}} outcomes with this net result",
    group_one: "One outcome with this net result",
    judgementTitle:
      "{{probability}} probability of a judgment balance of {{judgementValue}}",
    recoveries: {
      toPay: "Adverse costs",
    },
    feeTypes: {
      lawFirm: {
        cfaBase: "Law firm CFA base costs",
        cfaSuccess: "Law firm CFA success fee",
        cfaNoWin: "Law firm CFA no-win fee",
        recharge: "Barrister fees (passed on)",
      },
      barrister: {
        cfaBase: "Barrister CFA base costs",
        cfaSuccess: "Barrister CFA success fee",
        cfaNoWin: "Barrister CFA no-win fee",
      },
    },
    bill: {
      vat: "VAT",
      quantumAwarded: "{{name}} (awarded)",
      quantumToPay: "{{name}} (to pay)",
      enforcementShortfall: "Non-enforceable amount",
      settlementWith: "Settlement with {{name}}",
      settlementCoversSettlementCosts: "Settlement costs",
      settlementCoversAdverseCosts: "Costs towards {{name}}",
      settlementCoversOtherCosts: "Other costs",
      settlementCoversQuantum: "Applied to '{{name}}'",
      settlementLegalCost: "of which: settlement costs",
      subtotalOfWhichEnforcement: "of which: enforcement costs",
      costsSubtotal: "Subtotal",
      netCostSubtotal: "Net legal cost",
      recoveredColumn: "Recovered",
      amountColumn: "Amount",
    },
    costTypes,
    probability: "Probability",
    issueOutcomes: "Issue outcomes",
    noIssues: "No issue outcomes available",
    status_won: "Won",
    status_lost: "Lost",
    status_settled: "Settled",
    settledTooltip: "Settled for {{amount}}",
    financialBreakdown: "Financial breakdown",
    partyNetResult: "Party net result",
    lawFirmNetResult: "Law firm profit",
    // a client's firm, whose profit needs the fee structure
    lawFirmNetResultUnknown: "No fee structure entered",
    barristerNetResult: "Barrister profit",
    close: "Close",
  },
  agreement: {
    mainTitle: "Agreements — {{partyName}}",
    title: "Select agreement types",
    agreements: {
      lawFirmConditionalFeeAgreement:
        "Law firm conditional fee agreement (CFA)",
      barristerConditionalFeeAgreement:
        "Barrister conditional fee agreement (CFA)",
      damagesBasedAgreement: "Damages-based agreement (DBA)",
      thirdPartyFunding: "Third-party funding",
      afterTheEventInsurance: "After-the-event insurance (ATE)",
    },
    conditionalFeeAgreement: {
      section: {
        success: "Success conditions",
      },
      barristerFeesCovered: {
        yes: "Barrister fees covered",
        no: "Barrister fees not covered",
      },
      barristerCfaEngagedBy: {
        party: "Barrister engaged by party",
        lawFirm: "Barrister engaged by law firm",
      },
      successCondition: {
        step: "Step {{count}}",
        minimumCompensationAwarded: "Minimum awarded",
        minimumCompensationRecovered: "Minimum recovered",
        maximumCompensationToPay: "Maximum to pay",
      },
      successConditionType: {
        minimumCompensationAwarded: "Amount awarded",
        minimumCompensationRecovered: "Amount recovered",
        maximumCompensationToPay: "Amount to pay",
      },
      successFeePercentage: "Success fee percentage",
      successFee: "Success fee",
      addSuccessStep: "Add success step",
      configurator: {
        subtitle:
          "Set what the agreement covers and how the success fee steps with the quality of the win.",
        coveredBaseCosts: {
          title: "Covered base costs",
          baseCosts: "Base costs",
          barristerFees: "Barrister fees",
          fromBudget: "From the party's budget",
          barristerFeesIncluded: "Included in the covered base costs",
          barristerFeesExcluded: "Outside this agreement",
          barristerFeesUnderCfa: "Barrister fees under this CFA",
          engagement: "Engagement of the barrister",
          total: "Covered base costs:",
          ongoing:
            "As the case concludes, the base costs are calculated based on the actual spending.",
          barristerSuccess:
            "Includes the barrister's success fee at its highest step, assuming the barrister's CFA succeeds.",
        },
        lost: {
          title: "If the case is lost",
          noWinFee: "No-win fee",
          noWinNoFee: "{{percentage}} — no win, no fee",
          guaranteed: "Guaranteed base costs",
          lostBaseCosts: "Lost base costs",
          successFeeAppliesTo: "Success fee applies to",
        },
        precedence: {
          moot: "This agreement has no effect at present. The law firm's CFA covers barrister fees, and that takes precedence over a CFA between the party and the barrister: the law firm pays the barrister's regular fees. To give it effect, have the law firm engage the barrister or take barrister fees out of the law firm's CFA.",
          mootOnLawFirm:
            "The CFA between the party and the barrister has no effect while barrister fees are covered here.",
          recharged:
            "The law firm engages the barrister, but its own CFA does not cover barrister fees: it passes on to the party all it pays the barrister. This combination is no longer offered. Have the party engage the barrister — the party pays the same — or cover barrister fees in the law firm's CFA.",
          rechargedOnLawFirm:
            "The law firm engages the barrister under a CFA without covering barrister fees here, and passes on to the party all it pays the barrister. This combination is no longer offered: cover barrister fees here, or have the party engage the barrister.",
          coverLocked: {
            covered:
              "The law firm engages the barrister under a CFA, so barrister fees stay covered here. To take them out, switch the barrister's CFA off first.",
            notCovered:
              "The party has its own CFA with the barrister, so barrister fees cannot be covered here. To cover them, switch the barrister's CFA off first.",
          },
          engagementLocked: {
            lawFirm:
              "The law firm's CFA covers barrister fees, so the barrister is engaged by the law firm. To have the party engage the barrister, switch this agreement off first, then take barrister fees out of the law firm's CFA.",
            party:
              "The law firm's CFA does not cover barrister fees, so the barrister is engaged by the party. To have the law firm engage the barrister, switch this agreement off first, then cover barrister fees in the law firm's CFA.",
          },
        },
        triggeredBy: "Triggered by",
        ofAtRisk: "of at-risk",
        column: {
          step: "Step",
          rate: "Rate",
          uplift: "Uplift",
        },
        stepTotal: "total {{value}}",
        removeStep: "Delete step",
        range: {
          from: "{{from}} and above",
          between: "{{from}} to below {{to}}",
          nothingToPay: "Nothing to pay",
          upTo: "Up to {{upTo}}",
          aboveUpTo: "Above {{above}}, up to {{upTo}}",
        },
        warning: {
          unordered: "Each step must start above the one before it",
          unorderedToPay:
            "Each step must allow less to pay than the one before it",
          ineffective:
            "A step only changes the fee with a higher success fee than every step reached before it",
        },
        summary: {
          lost: "If lost",
          won: "If won",
          range: "{{minimum}} – {{maximum}}",
        },
        tab: {
          ladder: "Outcome ladder",
          readsAs: "Reads as",
        },
        footer: {
          unsaved:
            "Unsaved changes — figures above preview the draft. The agreement in the system is unchanged until you save.",
          save: "Save agreement",
        },
        ladder: {
          lost: "Case lost",
          lostWithFee: "No-win fee only — {{percentage}} of covered base costs",
          lostWithoutFee: "No fee due",
          nothingDue: "Nothing due — {{value}} of base costs lost",
          won: "Win · {{step}}",
          wonCaption: "{{trigger}} · {{range}} · {{percentage}} success fee",
          baseCosts: "Base costs · {{value}}",
          legend: {
            noWinFee: "No-win fee (guaranteed)",
            atRisk: "Base costs at risk",
            uplift: "Success fee uplift (shade per step)",
          },
        },
        readsAs: {
          coverage: {
            withBarrister:
              "The agreement covers base costs of {{baseCosts}} and barrister fees of {{barristerFees}} — {{covered}} in total.",
            withoutBarrister:
              "The agreement covers base costs of {{baseCosts}}. Barrister fees of {{barristerFees}} are outside this agreement.",
            barrister: {
              party:
                "The agreement covers barrister fees of {{covered}}. The barrister is engaged by the party.",
              lawFirm:
                "The agreement covers barrister fees of {{covered}}. The barrister is engaged by the law firm.",
            },
          },
          lostWithFee:
            "If the case is lost, a no-win fee of {{noWinFee}} is due ({{percentage}} of the covered base costs) and the remaining {{atRisk}} is lost.",
          lostWithoutFee:
            "If the case is lost, no fee is due and the covered base costs of {{covered}} are lost.",
          won: {
            minimumCompensationAwarded:
              "If the case is won, the covered base costs are due in full, plus a success fee on the {{atRisk}} at risk, at a rate set by the amount awarded:",
            minimumCompensationRecovered:
              "If the case is won, the covered base costs are due in full, plus a success fee on the {{atRisk}} at risk, at a rate set by the amount recovered:",
            maximumCompensationToPay:
              "If the case is won, the covered base costs are due in full, plus a success fee on the {{atRisk}} at risk, at a rate set by the amount to pay:",
          },
          step: "{{step}} — {{range}}: {{percentage}} success fee, {{uplift}} on top of the base costs, {{total}} in total.",
        },
      },
      cancel: "Cancel",
    },
    thirdPartyFunding: {
      caseDetails: "Case details",
      successCondition: "Success condition",
      fundingInvestment: "Funding investment",
      awardedDamages: "Awarded amount",
      funderProfit: "Funder profit calculation parameters",
      optionOneDescription: "A multiple of the investment (e.g., 3×).",
      optionTwoDescription: "A fee percentage applied to the awarded amount.",
      investmentMultiplier: "Multiplier (× investment)",
      resultingProfit: "Resulting profit",
      damagePercentage: "of the awarded amount",
      calculateFunding: {
        reimbursement: "Reimbursement: ",
        maxProfit: "Max profit: ",
      },
      payments: "Payment obligations",
      totalDueOnSuccess: "Total due on success: ",
      totalDueOnFailure: "Total due on failure: ",
      dialog: {
        title: "Funder profit calculation",
        dialogContent:
          "This agreement outlines the terms under which a funder provides financial support for a case. The funder is repaid the original investment plus profit if the case is successful. Profit is determined by taking the higher of either a multiple of the investment or a fee percentage of the awarded amount.",

        successFailure: "If the case fails, no repayment is required.",
        dialogOk: "OK",
      },
      caseDetailsDialog: {
        title: "Case details",
        descriptions: {
          successCondition:
            "The minimum award required for the case to be considered successful.",
          fundingInvestment:
            "The amount invested by the funder to finance the case.",
          awardedDamages:
            "Estimated amount awarded to the client if the case is successful.",
        },
      },
      paymentItems: {
        investmentPayment:
          "Funder is reimbursed the amount it actually invested in the case",
        profit: "Payment of the funder's profit",
      },
    },
    afterTheEventInsurance: {
      insuranceDetails: "Insurance details",
      premiumAmount: "Premium amount ",
      potentialLoss: "Potential loss ",
      paymentOption: "Payment option ",
      paymentOptionNote:
        "Select partial upfront to pay 30% now and the rest only upon success, or fully deferred to pay the entire premium only upon success.",
      partialUpfront: "Partial upfront (30%)",
      fullyDeferred: "Fully deferred",
      paymentObligation: "Payment obligations",
      totalDueOnSuccess: "Total due on success: ",
      totalDueOnFailure: "Total due on failure: ",
      toolTip: {
        premiumAmount: "The cost to insure against potential adverse costs.",
        potentialLoss: "The maximum loss to be insured against.",
      },
      dialog: {
        title: "Payment obligations",
        dialogContent:
          "Under a partial upfront arrangement, 30% of the premium is paid upfront. If the case is lost, only this amount is paid. However, if the case is successful, the full premium amount is payable (i.e. the upfront payment plus the remaining 70%). Under a fully deferred arrangement, no premium is paid unless the case is successful, in which case the entire premium amount is due.",
        dialogOk: "OK",
      },
      paymentItems: {
        payment: "Remainder of ATE premium is paid",
      },
    },
    noClients:
      "Agreements are configured for clients. Please configure a client party first",
    goToParties: "Go to parties section",
  },
  costInputDialog: {
    totalAmount: "Total amount",
    fixed: "{{amount}} apportioned in settlements",
    cancel: "Cancel",
    save: "Save disbursements",
    submit: "OK",
    costTypes,
  },
  agreementsAndWaterfall: {
    description:
      "Paid from the {{amount}} received, paid in this order of priority:",
  },
  varChart: {
    negativeValue:
      "{{probability}} probability of a {{currency}} result or worse",
    positiveValue: "{{probability}} probability of at least {{currency}}",
    axis: "Cumulative probability, worst result first",
    legend: {
      below: "Below zero",
      above: "Zero or above",
    },
  },
  costBarChart: {
    stages,
    total: "Total: {{amount}}",
    costTypes,
    incurred: "Incurred costs",
    estimated: "Estimated costs",
    droppedIncurred: "Incurred, apportioned in settlements",
    notToBeSpent: "Settled estimates",
  },
  costsPerStageSlider: {
    stages,
    sliderLabel: "{{progress}} {{stage}}",
  },
  toggleProgressBar: {
    noData: "No active progress data available.",
  },

  claimTypeChip: {
    claimTypes,
  },

  CostSummary: {
    total: "Total costs",
    totalWithVat: "Total costs incl. VAT",
    vat: "VAT at {{rate}}",
    lawFirmFees: "Law firm fees",
    disbursements: "Disbursements",
  },

  PortfolioFilterBar: {
    filter: "Filters",
    claimTypeFilter: {
      counter_zero: "Any claim types",
      counter: "{{count}}/{{total}} claim types",
    },
    agreementTypeFilter: {
      counter_zero: "Any agreements",
      counter: "{{count}}/{{total}} agreement types",
      agreementTypes,
    },
    settlementTypeFilter: {
      counter_zero: "Any settlements",
      counter: "{{count}}/{{total}} settlement types",
      settlementTypes: {
        none: "Not settled",
        settled: "Partially or fully settled",
      },
    },
  },
  ClaimTypeBreakdown: {
    cases: "{{count}} cases",
    cases_one: "One case",
    // a share too small to round to a whole percent
    tinyShare: "<1%",
    // all the values by all the cases
    average: "Average per case",
  },
  ClaimTypeAverages: {
    range: "Lowest case {{lowest}}, highest case {{highest}}",
    average: "Average {{average}}",
  },
  LawFirmPortfolio: {
    variant: {
      incurred: "Earned",
      estimated: "Estimated",
      total: "Total",
    },
    loss: "Loss",
    netResults: {
      title: "Expected profit",
      perCase: {
        title: "Expected profit per case",
        description:
          "Income minus expenses: what the firm expects to be left with from each client in each case.",
        profit: "Expected profit",
        loss: "Expected loss",
        margin: "Expected profit margin",
        marginOf: "of the revenue, earned and estimated",
        chart:
          "Each client's case, from the lowest expected profit to the highest. Select a bar to open its case.",
      },
    },
    progress: {
      title: "Case progress",
      measuredBy: "Progress measured by",
      average: "Average shown",
      showDistribution: "Show the distribution",
      hideDistribution: "Hide the distribution",
      noActiveCases_zero: "No active cases",
      noActiveCases_one: "No active cases (one case fully incurred)",
      noActiveCases: "No active cases ({{count}} cases fully incurred)",
      cost: {
        label: "By cost",
        title:
          "Percentage of costs incurred by your clients in {{count}} active cases",
        title_one:
          "Percentage of costs incurred by your clients in one active case",
        weighted: "Weighted by case total",
        flat: "Average per case",
        distribution:
          "Distribution of clients based on percentage of incurred costs in {{count}} active cases",
        distribution_one:
          "Distribution of clients based on percentage of incurred costs in one active case",
        clients: "Number of clients",
        inRange: {
          description: "{{count}} clients with {{range}} costs incurred",
          description_one:
            "One client in the range of {{range}} costs incurred",
        },
      },
      margin: {
        label: "By profit",
        title: "Percentage of profit earned in {{count}} active cases",
        title_one: "Percentage of profit earned in one active case",
        weighted: "Weighted by case total",
        flat: "Average per case",
        distribution:
          "Distribution of clients based on percentage of earned profit in {{count}} active cases",
        distribution_one:
          "Distribution of clients based on percentage of earned profit in one active case",
        clients: "Number of clients",
        inRange: {
          description: "{{count}} clients with {{range}} of profit earned",
          description_one:
            "One client in the range of {{range}} of earned profit",
        },
      },
    },
    revenue: {
      title: "Revenue",
      allCases: "All cases revenue",
      perClient: "Average revenue per client",
      byClaimType: {
        title: "Revenue by claim type",
        description:
          "The revenue of the cases with each set of claim types and its share of the whole, with the average revenue per case among them.",
        shown: "Revenue shown",
      },
      average: {
        title: "Average revenue by claim type",
        description:
          "The revenue per case of each set of claim types, from its lowest case to its highest and marked at the average.",
        shown: "Average revenue shown",
      },
    },
    margin: {
      title: "Profit",
      allCases: "All cases profit",
      perClient: "Average profit per client",
      byClaimType: {
        title: "Profit by claim type",
        description:
          "The profit of the cases with each set of claim types and its share of the whole, with the average profit per case among them.",
        shown: "Profit shown",
      },
      average: {
        title: "Average profit by claim type",
        description:
          "The profit per case of each set of claim types, from its lowest case to its highest and marked at the average.",
        shown: "Average profit shown",
      },
      stats: {
        title: "Profit stats",
        compared: "Profit compared",
        incurred: {
          max: "Highest profit earned",
          min: "Lowest profit earned",
        },
        estimated: {
          max: "Highest estimated profit",
          min: "Lowest estimated profit",
        },
        client: "{{partyName}} in '{{caseName}}'",
      },
    },
  },
  lawFirmRiskAnalysis: {
    title: "Law firm risk analysis",
    axis: "Cumulative probability, lowest profit first",
    income: "Income",
    expense: "Expenses",
    noOutcomes: "No outcomes for the case",
    profit: "Profit: {{value}}",
    loss: "Loss: {{value}}",
  },
  PartyOverviewCard: {
    assets: {
      unlimited: "Assets: Unlimited",
      value: "Assets: {{value}}",
    },
    costProgress: "Cost progress",
    winChance: "Win chance",
    status: "Status",
    settled: "Fully settled",
    netResult: "Average net result",
    agreements: {
      lawFirmCfa: "Law firm CFA",
      barristerCfa: "Barrister CFA",
    },
    tooltip: {
      winChance: "Case outcomes",
      status: "Settlements",
      netResult: "Net result",
    },
  },
  // an insurer's reserve of a claim (see shared/reserve.ts)
  reserve: {
    title: "Reserve",
    indicated: "Indicated reserve",
    caption: "Probability-weighted ultimate cost of the claim",
    unknown: "The reserve is worked out with the next calculation.",
    heads: {
      indemnity: "Indemnity",
      claimantCosts: "Claimant's costs",
      defenceCosts: "Defence costs",
      recoveries: "Recoveries",
    },
    page: {
      title: "Reserve",
      intro:
        "What the claim will cost: the reserve its outcomes indicate, the range of its ultimate cost, and the scenarios the reserve is made of.",
      noInsured: "The claim has no insured: mark one among the parties.",
      noOutcomes: "No outcomes for this party",
      indicated: "Indicated reserve",
      weighted: "Probability-weighted",
      weightedCaption: "The mean of all outcomes",
      likeliest: "Most likely",
      likeliestCaption: "The cost of the likeliest outcome, {{chance}}",
      paid: "Paid",
      outstanding: "Outstanding",
      range: "Range of the ultimate cost",
      median: "Median",
      maximum: "Maximum",
      scenarios: "Scenarios",
      grouping: "Group the outcomes",
      groupBy: {
        heads: "By heads of claim",
        cost: "By ultimate cost",
      },
      scenariosBy: {
        heads:
          "The outcomes grouped by how many of the heads of claim the insured loses, each with its chance and its cost",
        cost: "The outcomes grouped in equal bands of their ultimate cost, each with its chance and its cost",
      },
      headsLost: {
        none: "Defence succeeds",
        some: "Some heads of claim lost",
        all: "All heads of claim lost",
      },
      band: "{{from}} to {{to}}",
      scenario: "Scenario",
      chance: "Chance",
      ultimate: "Ultimate cost",
      weightedColumn: "Weighted",
    },
    history: {
      title: "Reserve history",
      intro:
        "How the indicated reserve moved, as recorded at each stage closed and settlement accepted, and once a day after other changes.",
      none: "No history yet: it starts with the next change of the case.",
      indicated: "Indicated reserve",
      latest: "Last recorded on {{date}}",
      onlyOne: "The first record",
      since: "{{movement}} since {{date}}",
      chartLabel: "The indicated reserve over time",
      movements: "Movements",
      column: {
        date: "Date",
        what: "What happened",
        stage: "Stage",
        indicated: "Indicated",
        movement: "Movement",
        by: "By",
      },
      imported: "Imported",
      importedBy: "{{name}}, imported",
      booked: {
        title: "Booked reserve",
        book: "Book reserve…",
        booked: "Booked",
        indicated: "Indicated now",
        difference: "Booked less indicated",
        above: "The booked reserve is {{share}} above the indicated",
        below: "The booked reserve is {{share}} below the indicated",
        on: "Booked on {{date}}",
        by: "by {{name}}",
        importedBy: "by {{name}}, imported",
        imported: "imported",
        none: "No reserve booked yet. Record the reserve your books hold to set it against the indicated one.",
        dialog: {
          title: "Book reserve",
          description:
            "The reserve booked in your books, to set against the indicated one. CaseSolv keeps it and posts it nowhere.",
          amount: "Amount",
          useIndicated: "Use indicated",
          negative:
            "The indicated reserve is below nothing: on average the claim is expected to bring the insured more than it costs, through damages or costs awarded to it or recoveries.",
          bookedOn: "Booked on",
          future: "A reserve cannot be booked after today",
          today: "Today",
          reason: "Reason (optional)",
          cancel: "Cancel",
          book: "Book reserve",
        },
      },
      event: {
        first: "First record",
        booked: "Reserve booked: {{amount}} on {{date}}",
        bookedFor:
          "Reserve booked: {{amount}} on {{date}}, \u201c{{reason}}\u201d",
        stageClosed: "{{stage}} closed",
        judgment: "Judgment",
        judgmentEdited: "Judgment changed",
        caseClosed: "Case closed",
        settlementAccepted:
          "Settlement of {{offeror}} with {{offeree}} accepted",
        settlementAcceptedUnknown: "Settlement accepted",
        changed: "Changed: {{fields}}",
      },
      field: {
        parties: "parties",
        claims: "claims",
        damages: "claimed amounts",
        budgets: "costs",
        settlements: "settlements",
        agreements: "agreements",
        timeline: "timeline",
        claimKind: "kind of claim",
      },
      note: "The history starts with the first record of the reserve; it holds no figures from before.",
    },
    sheet: {
      title: "Reserve sheet",
      titleOf: "Reserve sheet of {{name}}",
      description: "Paid, outstanding and incurred per head of the reserve",
      head: "Head",
      paid: "Paid",
      outstanding: "Outstanding",
      incurred: "Incurred",
      total: "Reserve",
    },
  },
  CostProgressBar: {
    progress: "{{percent}} of {{total}}",
    chip: {
      incurred: "Incurred {{value}}",
      estimated: "Estimated {{value}}",
    },
  },
  CaseOverview: {
    caseId: "Case '{{caseId}}'",
    createdAt: "Created: {{date}}",
    updatedAt: "Last update: {{date}}",
    // the plain variants above stay in use for cases with no recorded author
    createdAtBy: "Created: {{date}} by {{name}}",
    updatedAtBy: "Last update: {{date}} by {{name}}",
    delete: "Delete case",
    export: "Export case",
    changeTitle: "Change case title",
    profitCaption: "On the fees earned so far",
    // in place of the profit, which needs the fee structure
    feeStructureMissing: "No fee structure entered",
    settlement: {
      fullyCaption: "Nothing further is expected to happen in this case.",
    },
    exportDialog: {
      title: "Export case",
      description:
        "The exported file is encrypted with a password. You will need this password to import the case again. Keep it safe — it cannot be recovered.",
      password: "Password",
      confirmPassword: "Confirm password",
      tooShort: "Use at least {{min}} characters",
      mismatch: "Passwords do not match",
      cancel: "Cancel",
      download: "Download",
      error: "Failed to export case",
    },
    shortcuts: {
      claims: {
        title_zero: "No claims",
        title_one: "One claim",
        title_other: "{{count}} claims",
      },
      profit: "Profit",
    },
    claims: {
      claimed_one: "Claimed by client",
      claimed_other: "Claimed by clients",
      defended_one: "Defended by client",
      defended_other: "Defended by clients",
      other: "Other",
      totalAmountsClaimed: "Total claimed amounts",
    },
    claimTypes,
    claimTypeWithCount_one: "{{type}}",
    claimTypeWithCount_other: "{{count}}× {{type}}",
    headers: {
      lawFirm: "Law firm",
      parties: {
        clients: "{{count}} clients",
        clients_one: "One client",
        clients_zero: "No clients",
        opponents: "{{count}} opponents",
        opponents_one: "One opponent",
        opponents_zero: "No opponents",
        other: "{{count}} other parties",
        other_one: "Other party",
        other_zero: "No other parties",
      },
    },
  },
  EVAnalytics: {
    stages,
    stageRange: "{{from}} ↔ {{to}}",
    title: "Expected value",
    intro:
      "What the case is worth to a party on average at each stage, against the costs it has spent and still expects by then.",
    error: {
      noData: "No expected value data available",
    },
    evChartPaper: {
      title: "Expected value by stage",
      expectedValueAt: "Expected value at {{stage}}",
      costsByThen: "Costs by then",
      toggle: {
        evOnly: "Expected value only",
        settlementMidpoints: "Bargaining midpoints",
      },
    },
  },
  CasesList: {
    stages,
    claimTypes,
    agreementTypes,
    title: "Cases",
    import: "Import",
    newCase: "Add case",
    delete: "Delete case",
    deleteReadOnly: "You can view this case. You cannot delete it.",
    partyClient: "{{party}} (client)",
    searchPlaceholder: "Search by case title or party…",
    count: "<bold>{{shown}}</bold> of {{total}} cases",
    columns: {
      title: "Case",
      claimTypes: "Claim types",
      funding: "Agreements",
      stage: "Stage",
      value: "Claimed amounts",
      updatedAt: "Updated",
    },
    claimTypeFilters: {
      all: "All",
    },
    funding: {
      label: "Agreements",
      filters: {
        All: "All",
        none: "No agreement",
      },
    },
    settlement: {
      label: "Settlement",
      filters: {
        All: "All",
        None: "Not settled",
        Partial: "Partially settled",
        Settled: "Fully settled",
      },
      badge: {
        full: "Fully settled",
        partial: "Partially settled",
      },
    },
    judgment: {
      judged: "Judged",
      closed: "Closed",
    },
    pagination: {
      page: "Page {{page}} of {{total}}",
    },
  },
  guide: guideTerms,
};

i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    debug: true,
    fallbackLng: "en",
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    // the terms of a role are added over the base ones: what is drawn with
    // them is drawn again (see applyPersonaTerms)
    react: { bindI18nStore: "added" },
    resources: {
      en: {
        // a copy: i18next adds the terms of a role into the object it holds,
        // and the base terms must stay to bring back (see applyPersonaTerms)
        translation: JSON.parse(JSON.stringify(translationEn)),
      },
    },
  });

/** The terms of the app, as a law firm has them: the base of every role's. */
export type Terms = typeof translationEn;

// what the terms are drawn for: the role the user works in, and the kind of
// the claim that is open, if any
let termsOf: { persona: Persona; claimKind?: ClaimKind } = {
  persona: "lawFirm",
};

// The base terms, and over them those of the role and of the kind of claim.
function applyTerms(): void {
  const { persona, claimKind } = termsOf;
  const add = (terms: object) =>
    i18n.addResourceBundle("en", "translation", terms, true, true);
  add(translationEn);
  if (persona !== "insurer") return;
  add(insurerTerms);
  if (claimKind === "policyholderDispute") add(policyholderDisputeTerms);
}

/** Sets the terms to those of the role the user works in. */
export function applyPersonaTerms(persona: Persona): void {
  termsOf = { ...termsOf, persona };
  applyTerms();
}

/**
 * Sets the terms to those of the kind of the claim that is open, for an
 * insurer; undefined once none is open.
 */
export function applyClaimKindTerms(claimKind: ClaimKind | undefined): void {
  termsOf = { ...termsOf, claimKind };
  applyTerms();
}

export default i18n;
